import React, { useState, useEffect } from 'react';
import {
  Settings,
  Save,
  Globe,
  Upload,
  Image as ImageIcon,
  CreditCard,
  Mail,
  Sliders,
  Wrench,
  ShieldCheck,
  MapPin,
  Phone,
  MessageSquare,
  Building2,
  FileText,
  Hash,
  UserCheck,
  Search,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Sparkles,
  Eye,
  EyeOff,
  CheckCircle2,
  Grid,
  Layers,
  Loader2,
  Clock,
  User,
  Lock,
  Monitor,
  Smartphone,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { broadcastSyncEvent } from '../services/api';
import { resolveImageUrl, isValidAvatarUrl } from '../utils/imageUtils';
import MaintenancePage from './MaintenancePage';

const DEFAULT_MOBILE_SECTIONS = [
  { id: 'parent_categories', title: 'Quick Categories', subtitle: '', enabled: true, layout: 'horizontal', order: 1 },
  { id: 'hero_banners', title: 'Promotional Banners', subtitle: '', enabled: true, layout: 'horizontal', order: 2 },
  { id: 'trust_badges', title: 'Trust & Delivery Badges', subtitle: '', enabled: true, layout: 'horizontal', order: 3 },
  { id: 'categories_style', title: 'Shop Your Style', subtitle: '', enabled: true, layout: 'horizontal', order: 4 },
  { id: 'flash_picks', title: 'Flash Picks', subtitle: 'Ends in 02 : 41 : 36', enabled: true, layout: 'horizontal', order: 5 },
  { id: 'complete_look', title: 'Complete The Look', subtitle: 'Curated combos for you', enabled: true, layout: 'horizontal', order: 6 },
  { id: 'shop_by_occasion', title: 'Shop by Occasion', subtitle: '', enabled: true, layout: 'horizontal', order: 7 },
  { id: 'find_your_price', title: 'Find Your Price', subtitle: '', enabled: true, layout: 'horizontal', order: 8 },
  { id: 'recommended', title: 'Recommended For You', subtitle: '', enabled: true, layout: 'horizontal', order: 9 },
  { id: 'trending', title: 'Trending Now', subtitle: 'Popular styles customers are loving', enabled: true, layout: 'vertical', order: 10 },
  { id: 'new_arrivals', title: 'New Arrivals', subtitle: 'Explore the latest fashion collections', enabled: true, layout: 'horizontal', order: 11 },
  { id: 'best_sellers', title: 'Best Sellers', subtitle: 'Top rated favorites loved by everyone', enabled: true, layout: 'vertical', order: 12 },
  { id: 'continue_shopping', title: 'Continue Shopping', subtitle: '', enabled: true, layout: 'horizontal', order: 13 }
];

const DEFAULT_PRODUCT_SECTIONS = [
  { id: 'recommended', section_key: 'recommended', title: 'Recommended For You', subtitle: 'Handpicked selections based on your style', enabled: true, position: 1, desktop_layout: 'carousel_2_rows', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'View All →', view_all_link: '/shop' },
  { id: 'new_arrivals', section_key: 'new_arrivals', title: 'New Arrivals', subtitle: 'Explore the latest fashion drops & arrivals', enabled: true, position: 2, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'View All →', view_all_link: '/shop?filter=new' },
  { id: 'featured', section_key: 'featured', title: 'Featured Products', subtitle: 'Curated premium items handpicked for you', enabled: true, position: 3, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'View All →', view_all_link: '/shop?filter=featured' },
  { id: 'trending', section_key: 'trending', title: 'Trending Now', subtitle: 'Popular styles customers are loving right now', enabled: true, position: 4, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'grid_2_col', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'View All →', view_all_link: '/shop?filter=trending' },
  { id: 'most_loved', section_key: 'most_loved', title: 'Most-Loved Fashion for You', subtitle: 'Top-rated favorites handpicked for your style', enabled: true, position: 5, desktop_layout: 'grid', desktop_product_count: 8, desktop_max_products: 16, mobile_layout: 'grid_2_col', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'View All →', view_all_link: '/shop?filter=loved' },
  { id: 'starting_199', section_key: 'starting_199', title: 'Starting @ ₹199', subtitle: 'Unbeatable value on budget-friendly fashion & essentials', enabled: true, position: 6, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'Explore Under ₹199 →', view_all_link: '/shop?maxPrice=399' },
  { id: 'best_sellers', section_key: 'best_sellers', title: 'Best Sellers', subtitle: 'Customer favorite picks & top-rated items', enabled: true, position: 7, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'grid_2_col', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'View All →', view_all_link: '/shop?filter=bestsellers' },
  { id: 'flash_picks', section_key: 'flash_picks', title: 'Flash Picks', subtitle: 'Limited-time deals on trending products', enabled: true, position: 8, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'View All Deals →', view_all_link: '/shop?filter=offers' },
  { id: 'complete_look', section_key: 'complete_look', title: 'Complete The Look', subtitle: 'Curated style combos matched for you', enabled: true, position: 9, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'View Combos →', view_all_link: '/shop?filter=combos' },
  { id: 'popular_picks', section_key: 'popular_picks', title: 'Popular Products', subtitle: 'Most viewed & saved items this week', enabled: true, position: 10, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'View All →', view_all_link: '/shop?filter=popular' },
  { id: 'top_offers', section_key: 'top_offers', title: 'Top Offers & Discounts', subtitle: 'Steal deals with up to 60% off', enabled: true, position: 11, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'Shop Offers →', view_all_link: '/shop?filter=offers' },
  { id: 'todays_deal', section_key: 'todays_deal', title: "Today's Special Deal", subtitle: 'Exclusive 24-hour price drop on selected items', enabled: true, position: 12, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: "Today's Deals →", view_all_link: '/shop?filter=offers' },
  { id: 'shop_by_occasion', section_key: 'shop_by_occasion', title: 'Shop by Occasion', subtitle: 'Outfits & accessories for every event', enabled: true, position: 13, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'Explore Occasions →', view_all_link: '/shop' },
  { id: 'find_your_price', section_key: 'find_your_price', title: 'Find Your Price Range', subtitle: 'Shop products grouped by budget', enabled: true, position: 14, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'All Price Ranges →', view_all_link: '/shop' },
  { id: 'premium_collection', section_key: 'premium_collection', title: 'Premium Store & 925 Silver', subtitle: 'Luxury high-end fashion & hallmarked silver', enabled: true, position: 15, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'Explore Premium →', view_all_link: '/shop?category=Jewellery' },
  { id: 'mens_collection', section_key: 'mens_collection', title: "Men's Collection", subtitle: 'T-Shirts, Shirts, Sneakers & Casual Wear for Men', enabled: false, position: 16, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: "Shop Men's →", view_all_link: '/shop?category=Men' },
  { id: 'womens_collection', section_key: 'womens_collection', title: "Women's Collection", subtitle: 'Ethic wear, Kurtas, Sarees & Western outfits', enabled: false, position: 17, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: "Shop Women's →", view_all_link: '/shop?category=Women' },
  { id: 'kids_collection', section_key: 'kids_collection', title: 'Kids & Baby Collection', subtitle: 'Cute prints & comfortable clothing for kids', enabled: false, position: 18, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'Shop Kids →', view_all_link: '/shop?category=Kids' },
  { id: 'unisex_collection', section_key: 'unisex_collection', title: 'Unisex Collection', subtitle: 'Streetwear & oversized fits designed for everyone', enabled: true, position: 19, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'Shop Unisex →', view_all_link: '/shop?category=Unisex' },
  { id: 'jewellery_collection', section_key: 'jewellery_collection', title: 'Jewellery & Jewels', subtitle: '925 Sterling Silver rings, pendants & accessories', enabled: true, position: 20, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'Shop Jewellery →', view_all_link: '/shop?category=Jewellery' },
  { id: 'accessories_collection', section_key: 'accessories_collection', title: 'Accessories Collection', subtitle: 'Bags, Sunglasses, Caps & Lifestyle Essentials', enabled: false, position: 21, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'Shop Accessories →', view_all_link: '/shop?category=Accessories' },
  { id: 'fresh_summer', section_key: 'fresh_summer', title: 'Fresh Summer Looks', subtitle: 'Lightweight linen & vibrant summer apparel', enabled: true, position: 22, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'Shop Summer →', view_all_link: '/shop?filter=summer' },
  { id: 'recently_viewed', section_key: 'recently_viewed', title: 'Recently Viewed', subtitle: 'Pick up right where you left off', enabled: true, position: 23, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'View All →', view_all_link: '/shop' },
  { id: 'related_products', section_key: 'related_products', title: 'Related & Similar Products', subtitle: 'Matches based on items you explored', enabled: true, position: 24, desktop_layout: 'carousel', desktop_product_count: 6, desktop_max_products: 12, mobile_layout: 'carousel', mobile_product_count: 6, mobile_max_products: 12, show_view_all: true, view_all_text: 'View All →', view_all_link: '/shop' }
];

export default function AdminSettingsPage() {
  const [activeTab, setActiveTab] = useState('company');
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
  const [showMaintenancePreviewModal, setShowMaintenancePreviewModal] = useState(false);
  const [previewDeviceMode, setPreviewDeviceMode] = useState('desktop');

  const [productSections, setProductSections] = useState(DEFAULT_PRODUCT_SECTIONS);
  const [productSectionSearch, setProductSectionSearch] = useState('');
  const [savingProductSections, setSavingProductSections] = useState(false);

  const fetchProductSections = async () => {
    try {
      const res = await api.get('/homepage-sections/admin').catch(() => null);
      const data = res?.data?.data || res?.data;
      const list = data?.sections || data;
      if (Array.isArray(list) && list.length > 0) {
        setProductSections(list.sort((a, b) => (Number(a.position || a.display_order) || 0) - (Number(b.position || b.display_order) || 0)));
      }
    } catch (e) {
      console.error('Error fetching product sections:', e);
    }
  };

  const handleSaveProductSections = async () => {
    setSavingProductSections(true);
    try {
      const reordered = productSections.map((sec, idx) => ({ ...sec, position: idx + 1, display_order: idx + 1 }));
      await api.post('/homepage-sections/admin', { sections: reordered });
      setProductSections(reordered);
      toast.success('Product Section Layout Controls saved successfully!');
      window.dispatchEvent(new window.Event('karviyam_homepage_sections_updated'));
      broadcastSyncEvent('karviyam_homepage_sections_updated');
    } catch (e) {
      toast.error('Failed to save section configurations.');
    } finally {
      setSavingProductSections(false);
    }
  };

  const handleResetProductSections = async () => {
    setProductSections(DEFAULT_PRODUCT_SECTIONS);
    try {
      await api.post('/homepage-sections/admin', { sections: DEFAULT_PRODUCT_SECTIONS });
      toast.success('Reset to default section layout settings!');
      window.dispatchEvent(new window.Event('karviyam_homepage_sections_updated'));
      broadcastSyncEvent('karviyam_homepage_sections_updated');
    } catch (e) {}
  };

  const handleMoveProductSection = (idx, direction) => {
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= productSections.length) return;
    const updated = [...productSections];
    const [moved] = updated.splice(idx, 1);
    updated.splice(targetIdx, 0, moved);
    const reordered = updated.map((sec, i) => ({ ...sec, position: i + 1, display_order: i + 1 }));
    setProductSections(reordered);
  };

  const handleUpdateProductSection = (id, patch) => {
    setProductSections(prev => prev.map(s => (s.id === id || s.section_key === id) ? { ...s, ...patch } : s));
  };

  useEffect(() => {
    fetchProductSections();
  }, []);

  const [mobileSections, setMobileSections] = useState(() => {
    try {
      const saved = localStorage.getItem('karviyam_mobile_homepage_sections');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {}
    return DEFAULT_MOBILE_SECTIONS;
  });

  // Email Notification Settings State
  const [emailSettings, setEmailSettings] = useState({
    emailNotificationsEnabled: true,
    enableOrderPlacedEmail: true,
    enableStatusUpdateEmail: true,
    enableOutForDeliveryEmail: true,
    enableDeliveredEmail: true,
    enableCancelledEmail: true,
    enableRefundEmail: true
  });

  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('ORDER_PLACED');
  const [emailLogs, setEmailLogs] = useState([]);
  const [savingEmailSettings, setSavingEmailSettings] = useState(false);

  // Email Preview Modal State
  const [showOrderEmailPreviewModal, setShowOrderEmailPreviewModal] = useState(false);
  const [emailPreviewSubject, setEmailPreviewSubject] = useState('');
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchEmailNotificationSettings = async () => {
    try {
      const res = await api.get('/admin/email-notifications/settings').catch(() => null);
      const data = res?.data?.data || res?.data;
      if (data) {
        if (data.settings) setEmailSettings(data.settings);
        if (Array.isArray(data.templates) && data.templates.length > 0) setEmailTemplates(data.templates);
        if (Array.isArray(data.logs)) setEmailLogs(data.logs);
      }
    } catch (e) {
      console.error('Error fetching email notification settings:', e);
    }
  };

  const handleSaveEmailNotificationSettings = async () => {
    setSavingEmailSettings(true);
    toast.loading('Saving email notification settings & templates...', { id: 'email-settings-toast' });
    try {
      await api.post('/admin/email-notifications/settings', {
        settings: emailSettings,
        templates: emailTemplates
      });
      toast.success('Email notification settings & templates saved successfully!', { id: 'email-settings-toast' });
      await fetchEmailNotificationSettings();
    } catch (e) {
      toast.error('Failed to save email settings.', { id: 'email-settings-toast' });
    } finally {
      setSavingEmailSettings(false);
    }
  };

  const handlePreviewOrderEmail = async (template) => {
    setPreviewLoading(true);
    setShowOrderEmailPreviewModal(true);
    try {
      const payload = {
        templateKey: template.template_key || template.templateKey || selectedTemplateKey,
        subject: template.subject,
        heading: template.heading,
        bodyHtml: template.body_html || template.bodyHtml,
        buttonText: template.button_text || template.buttonText,
        buttonUrl: template.button_url || template.buttonUrl
      };
      const res = await api.post('/admin/email-notifications/preview', payload);
      const data = res?.data?.data || res?.data;
      if (data) {
        setEmailPreviewSubject(data.subject || '');
        setEmailPreviewHtml(data.html || '');
      }
    } catch (e) {
      toast.error('Failed to generate email preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleUpdateTemplate = (key, field, val) => {
    setEmailTemplates(prev => prev.map(t => (t.template_key === key || t.templateKey === key) ? { ...t, [field]: val } : t));
  };

  const handleToggleSection = (id) => {
    setMobileSections(prev => prev.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s));
  };

  const handleChangeSectionLayout = (id, layout) => {
    setMobileSections(prev => prev.map(s => s.id === id ? { ...s, layout } : s));
    if (id === 'recommended') setSettings(s => ({ ...s, mobileRecommendedMode: layout }));
    if (id === 'trending') setSettings(s => ({ ...s, mobileTrendingMode: layout }));
    if (id === 'new_arrivals') setSettings(s => ({ ...s, mobileNewArrivalsMode: layout }));
    if (id === 'best_sellers') setSettings(s => ({ ...s, mobileBestSellersMode: layout }));
  };

  const handleChangeSectionTitle = (id, title) => {
    setMobileSections(prev => prev.map(s => s.id === id ? { ...s, title } : s));
  };

  const handleChangeSectionSubtitle = (id, subtitle) => {
    setMobileSections(prev => prev.map(s => s.id === id ? { ...s, subtitle } : s));
  };

  const handleMoveSection = (index, direction) => {
    const newIdx = index + direction;
    if (newIdx < 0 || newIdx >= mobileSections.length) return;
    const updated = [...mobileSections];
    const [moved] = updated.splice(index, 1);
    updated.splice(newIdx, 0, moved);
    const reordered = updated.map((sec, idx) => ({ ...sec, order: idx + 1 }));
    setMobileSections(reordered);
  };

  const [settings, setSettings] = useState({
    storeName: 'Karviyam Ventures Private Limited',
    legalCompanyName: 'Karviyam Ventures Private Limited',
    gstNo: '33AAACK1234F1Z9',
    panNo: 'AAACK1234F',
    cinNo: 'U74999TN2026PTC123456',
    state: 'Tamil Nadu',
    stateCode: '33',
    address: 'Karviyam Tower, Park Avenue, Chennai, Tamil Nadu 600001',
    warehouseAddress: 'Karviyam Logistics Park, Erode, Tamil Nadu 638001',
    supportEmail: 'vanakkam@karviyam.com',
    supportPhone: '+91 98765 43210',
    website: 'www.karviyam.com',
    signatoryName: 'Karviyam Operations',
    signatoryDesignation: 'Authorized Signatory',

    orderPrefix: 'KV-ORD-',
    orderNextSeq: '1',
    invoicePrefix: 'KAR-',
    invoiceNextSeq: '1',

    codEnabled: true,
    razorpayEnabled: true,
    upiQrEnabled: true,
    stripeEnabled: true,
    onlinePaymentEnabled: true,
    defaultPaymentMethod: 'COD',

    footerAbout: 'Karviyam is a premium marketplace destination for high-street streetwear, 925 sterling silver jewellery, luxury kicks, and lifestyle products.',
    announcementText: 'FESTIVE SALE IS LIVE! UP TO 60% OFF ON HIGH-STREET WEAR & FINE JEWELLERY.',
    logoUrl: '',
    emailLogoUrl: '',
    maxProductImages: '6',

    categoryNavigationEnabled: true,

    recommendedScrollMode: 'grid',
    newArrivalsScrollMode: 'carousel',
    featuredScrollMode: 'carousel',
    removeImageGreyBox: true,

    productImageAutoChange: false,
    productImageChangeInterval: 3,

    loginPopupEnabled: true,
    loginPopupDelaySeconds: 5,

    maintenanceMode: false,
    maintenanceTitle: "We'll Be Right Back!",
    maintenanceSubtitle: "SYSTEM UNDER MAINTENANCE",
    maintenanceMessage: 'Karviyam is currently undergoing scheduled platform maintenance to bring you exciting new drops! We will be back online shortly.',
    maintenanceEstimatedTime: 'Estimated Uptime: Back Online Soon',
    maintenanceShowTimer: true,
    maintenanceShowSocial: true,
    maintenanceAllowSearchEngines: true,
    maintenanceDeveloperEnabled: true,
    maintenanceDeveloperLogoUrl: '',
    maintenanceDeveloperName: 'CraftLoop',
    maintenanceDeveloperLink: '',
  });

  const [adminPhotoUrl, setAdminPhotoUrl] = useState(() => localStorage.getItem('karviyam_admin_photo') || '');
  const [uploadingAdminPhoto, setUploadingAdminPhoto] = useState(false);
  const [systemHealth, setSystemHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [uploadingDevLogo, setUploadingDevLogo] = useState(false);

  const fetchSystemHealth = async () => {
    setLoadingHealth(true);
    try {
      const res = await api.get('/admin/system-health');
      if (res.data?.success) {
        setSystemHealth(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch system health:', err);
    } finally {
      setLoadingHealth(false);
    }
  };

  useEffect(() => {
    const fetchAdminPhoto = async () => {
      try {
        const res = await api.get('/admin/profile');
        if (res.data?.success && res.data.data?.photoUrl) {
          setAdminPhotoUrl(res.data.data.photoUrl);
          localStorage.setItem('karviyam_admin_photo', res.data.data.photoUrl);
        }
      } catch (e) {}
    };
    fetchAdminPhoto();
  }, []);

  const handleAdminPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    const toastId = toast.loading('Uploading admin profile photo...');
    setUploadingAdminPhoto(true);
    try {
      const res = await api.post('/admin/profile/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data && res.data.success) {
        toast.success('Admin profile photo updated successfully!', { id: toastId });
        const newPhoto = res.data.data.photoUrl;
        setAdminPhotoUrl(newPhoto);
        localStorage.setItem('karviyam_admin_photo', newPhoto);
        window.dispatchEvent(new window.Event('karviyam_admin_photo_updated'));
      } else {
        toast.error(res.data?.message || 'Photo upload failed', { id: toastId });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload admin photo', { id: toastId });
    } finally {
      setUploadingAdminPhoto(false);
    }
  };

  const handleRemoveAdminPhoto = async () => {
    const toastId = toast.loading('Removing admin profile photo...');
    try {
      const res = await api.post('/admin/profile/photo/remove');
      if (res.data && res.data.success) {
        toast.success('Admin profile photo removed', { id: toastId });
        setAdminPhotoUrl('');
        localStorage.removeItem('karviyam_admin_photo');
        window.dispatchEvent(new window.Event('karviyam_admin_photo_updated'));
      }
    } catch (err) {
      toast.error('Failed to remove photo', { id: toastId });
    }
  };

  useEffect(() => {
    try {
      const localLayouts = localStorage.getItem('karviyam_section_layouts');
      if (localLayouts) {
        const parsed = JSON.parse(localLayouts);
        setSettings(prev => ({
          ...prev,
          desktopRecommendedMode: parsed.desktop?.recommended || 'carousel',
          desktopNewArrivalsMode: parsed.desktop?.newArrivals || 'carousel',
          desktopFeaturedMode: parsed.desktop?.featured || 'carousel',
          mobileRecommendedMode: parsed.mobile?.recommended || parsed.recommended || 'horizontal',
          mobileTrendingMode: parsed.mobile?.trending || parsed.trending || 'vertical',
          mobileNewArrivalsMode: parsed.mobile?.newArrivals || 'horizontal',
          mobileBestSellersMode: parsed.mobile?.bestSellers || 'vertical',
          removeImageGreyBox: parsed.removeGreyBox !== false
        }));
      }

      const savedMob = localStorage.getItem('karviyam_mobile_homepage_sections');
      if (savedMob) {
        const parsedMob = JSON.parse(savedMob);
        if (Array.isArray(parsedMob) && parsedMob.length > 0) setMobileSections(parsedMob);
      }
    } catch (e) {}
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const compRes = await api.get('/settings/company').catch(() => null);
      const compData = compRes?.data?.data || compRes?.data;

      const payRes = await api.get('/settings/payment').catch(() => null);
      const payData = payRes?.data?.data || payRes?.data || {};

      const res = await api.get('/settings').catch(() => null);
      const apiData = res?.data ? res.data : (res || {});
      const dataMap = apiData.data || apiData;

      if (dataMap && typeof dataMap === 'object') {
        const checkB = (val, defaultVal = true) => {
          if (val === undefined || val === null) return defaultVal;
          if (typeof val === 'boolean') return val;
          if (typeof val === 'number') return val === 1;
          if (typeof val === 'string') {
            const l = val.trim().toLowerCase();
            if (l === 'true' || l === '1') return true;
            if (l === 'false' || l === '0') return false;
          }
          return defaultVal;
        };

        const codVal = payData.codEnabled !== undefined ? payData.codEnabled : (dataMap.codEnabled !== undefined ? dataMap.codEnabled : payData.cod_enabled);
        const onlineVal = payData.onlinePaymentEnabled !== undefined ? payData.onlinePaymentEnabled : (dataMap.onlinePaymentEnabled !== undefined ? dataMap.onlinePaymentEnabled : payData.online_payment_enabled);
        const rzpVal = payData.razorpayEnabled !== undefined ? payData.razorpayEnabled : (dataMap.razorpayEnabled !== undefined ? dataMap.razorpayEnabled : payData.razorpay_enabled);
        const upiQrVal = payData.upiQrEnabled !== undefined ? payData.upiQrEnabled : (dataMap.upiQrEnabled !== undefined ? dataMap.upiQrEnabled : (payData.upi_qr_enabled !== undefined ? payData.upi_qr_enabled : payData.enable_upi_qr));
        const stpVal = payData.stripeEnabled !== undefined ? payData.stripeEnabled : (dataMap.stripeEnabled !== undefined ? dataMap.stripeEnabled : payData.stripe_enabled);
        const defVal = payData.defaultPaymentMethod || dataMap.defaultPaymentMethod || payData.default_payment_method;
        const catNavVal = dataMap.categoryNavigationEnabled !== undefined ? dataMap.categoryNavigationEnabled : dataMap.category_navigation_enabled;

        const mobData = dataMap.karviyam_mobile_homepage_sections || dataMap.mobile_homepage_sections;
        if (mobData) {
          try {
            const parsedMob = typeof mobData === 'string' ? JSON.parse(mobData) : mobData;
            if (Array.isArray(parsedMob) && parsedMob.length > 0) {
              setMobileSections(parsedMob);
              localStorage.setItem('karviyam_mobile_homepage_sections', JSON.stringify(parsedMob));
            }
          } catch (e) {}
        }

        setSettings(prev => ({
          ...prev,
          storeName: compData?.companyDisplayName || dataMap.storeName || prev.storeName,
          legalCompanyName: compData?.legalCompanyName || dataMap.legalCompanyName || dataMap.storeName || prev.legalCompanyName,
          gstNo: compData?.gstNumber || dataMap.gstNo || prev.gstNo,
          panNo: compData?.panNumber || dataMap.panNo || prev.panNo,
          cinNo: compData?.cinNumber || dataMap.cinNo || prev.cinNo,
          state: compData?.state || dataMap.state || prev.state,
          stateCode: compData?.stateCode || dataMap.stateCode || prev.stateCode,
          address: compData?.registeredAddress || dataMap.address || prev.address,
          warehouseAddress: compData?.warehouseAddress || dataMap.warehouseAddress || prev.warehouseAddress,
          supportEmail: compData?.supportEmail || dataMap.supportEmail || prev.supportEmail,
          supportPhone: compData?.supportPhone || dataMap.supportPhone || prev.supportPhone,
          website: compData?.website || dataMap.website || prev.website,
          signatoryName: compData?.authorizedSignatory || dataMap.signatoryName || prev.signatoryName,
          signatoryDesignation: compData?.designation || dataMap.signatoryDesignation || prev.signatoryDesignation,
          orderPrefix: dataMap.orderPrefix || prev.orderPrefix,
          orderNextSeq: dataMap.orderNextSeq || prev.orderNextSeq,
          invoicePrefix: dataMap.invoicePrefix || prev.invoicePrefix,
          invoiceNextSeq: dataMap.invoiceNextSeq || prev.invoiceNextSeq,

          codEnabled: checkB(codVal, true),
          razorpayEnabled: checkB(rzpVal, true),
          upiQrEnabled: checkB(upiQrVal, true),
          stripeEnabled: checkB(stpVal, true),
          onlinePaymentEnabled: checkB(onlineVal, true),
          defaultPaymentMethod: defVal || prev.defaultPaymentMethod,
          categoryNavigationEnabled: checkB(catNavVal, true),

          productImageAutoChange: checkB(dataMap.productImageAutoChange !== undefined ? dataMap.productImageAutoChange : dataMap.product_image_auto_change, false),
          productImageChangeInterval: parseInt(dataMap.productImageChangeInterval || dataMap.product_image_change_interval || 3, 10) || 3,

          loginPopupEnabled: checkB(dataMap.loginPopupEnabled !== undefined ? dataMap.loginPopupEnabled : dataMap.login_popup_enabled, true),
          loginPopupDelaySeconds: parseInt(dataMap.loginPopupDelaySeconds || dataMap.login_popup_delay_seconds || 5, 10) || 5,

          footerAbout: dataMap.footerAbout || prev.footerAbout,
          announcementText: dataMap.announcementText || prev.announcementText,
          logoUrl: dataMap.logoUrl || prev.logoUrl,
          emailLogoUrl: dataMap.email_logo_url || dataMap.emailLogoUrl || prev.emailLogoUrl,
          maxProductImages: dataMap.maxProductImages || prev.maxProductImages,
          maintenanceMode: dataMap.maintenanceMode === 'true' || dataMap.maintenanceMode === true,
          maintenanceLogoUrl: dataMap.maintenanceLogoUrl || dataMap.maintenance_logo_url || prev.maintenanceLogoUrl || '',
          maintenanceTitle: dataMap.maintenanceTitle || prev.maintenanceTitle,
          maintenanceSubtitle: dataMap.maintenanceSubtitle || prev.maintenanceSubtitle,
          maintenanceMessage: dataMap.maintenanceMessage || prev.maintenanceMessage,
          maintenanceEstimatedTime: dataMap.maintenanceEstimatedTime || prev.maintenanceEstimatedTime,
          maintenanceShowTimer: dataMap.maintenanceShowTimer !== 'false',
          maintenanceShowSocial: dataMap.maintenanceShowSocial !== 'false',
          maintenanceAllowSearchEngines: dataMap.maintenanceAllowSearchEngines !== 'false',
          maintenanceDeveloperEnabled: checkB(dataMap.maintenanceDeveloperEnabled !== undefined ? dataMap.maintenanceDeveloperEnabled : dataMap.maintenance_developer_enabled, true),
          maintenanceDeveloperLogoUrl: dataMap.maintenanceDeveloperLogoUrl || dataMap.maintenance_developer_logo_url || prev.maintenanceDeveloperLogoUrl || '',
          maintenanceDeveloperName: dataMap.maintenanceDeveloperName || dataMap.maintenance_developer_name || prev.maintenanceDeveloperName || 'CraftLoop',
          maintenanceDeveloperLink: dataMap.maintenanceDeveloperLink || dataMap.maintenance_developer_link || prev.maintenanceDeveloperLink || '',
        }));

      }
    } catch (e) {
      console.error(e);
    }
  };

  const notifyChanges = () => {
    window.dispatchEvent(new window.Event('karviyam_logo_updated'));
    window.dispatchEvent(new window.Event('karviyam_maintenance_updated'));
    window.dispatchEvent(new window.Event('karviyam_footer_updated'));
    window.dispatchEvent(new window.Event('karviyam_settings_updated'));
    window.dispatchEvent(new window.Event('karviyam_category_nav_updated'));
    window.dispatchEvent(new window.Event('karviyam_section_layouts_updated'));
    window.dispatchEvent(new window.Event('karviyam_mobile_homepage_updated'));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Logo file size must be less than 5MB');
        return;
      }
      const reader = new window.FileReader();
      reader.onload = () => {
        const base64Logo = reader.result;
        setSettings(prev => ({ ...prev, logoUrl: base64Logo }));
        toast.success('Company logo selected!');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEmailLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
        toast.error('Invalid image type. Please upload a PNG, JPG, or WebP image.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Email logo file size must be less than 5MB');
        return;
      }
      const reader = new window.FileReader();
      reader.onload = () => {
        const base64Logo = reader.result;
        setSettings(prev => ({ ...prev, emailLogoUrl: base64Logo }));
        toast.success('Custom email logo loaded! Click Save Settings to apply.');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveEmailLogo = () => {
    setSettings(prev => ({ ...prev, emailLogoUrl: '' }));
    toast.success('Custom email logo removed. Default Karviyam logo will be used.');
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (uploadingDevLogo || uploadingAdminPhoto) {
      toast.error('Image upload is currently in progress. Please wait a moment for the upload to complete before saving.');
      return;
    }

    if ((settings.maintenanceDeveloperLogoUrl || '').startsWith('blob:')) {
      toast.error('Cannot save settings: Developer Logo contains an unpersisted temporary blob URL. Please upload the file again or enter a valid URL.');
      return;
    }
    if ((settings.maintenanceLogoUrl || '').startsWith('blob:')) {
      toast.error('Cannot save settings: Maintenance Logo contains an unpersisted temporary blob URL. Please upload the file again or enter a valid URL.');
      return;
    }

    // Frontend Validations
    if (!settings.storeName || !settings.storeName.trim()) {
      toast.error('Company Display Name is required');
      return;
    }
    if (!settings.legalCompanyName || !settings.legalCompanyName.trim()) {
      toast.error('Legal Company Name is required');
      return;
    }
    if (!settings.gstNo || !settings.gstNo.trim()) {
      toast.error('GST Number (GSTIN) is required');
      return;
    }
    if (!settings.panNo || !settings.panNo.trim()) {
      toast.error('PAN Number is required');
      return;
    }
    if (!settings.state || !settings.state.trim()) {
      toast.error('State is required');
      return;
    }
    if (!settings.stateCode || !settings.stateCode.trim()) {
      toast.error('State Code is required');
      return;
    }
    if (!settings.address || !settings.address.trim()) {
      toast.error('Registered Address is required');
      return;
    }
    if (!settings.supportEmail || !settings.supportEmail.trim()) {
      toast.error('Support Email is required');
      return;
    }
    if (!settings.supportPhone || !settings.supportPhone.trim()) {
      toast.error('Support Phone is required');
      return;
    }

    try {
      // 1. Save dedicated Company Settings entity to MySQL (company_settings table)
      const companyPayload = {
        companyDisplayName: settings.storeName.trim(),
        legalCompanyName: settings.legalCompanyName.trim(),
        gstNumber: settings.gstNo.trim(),
        panNumber: settings.panNo.trim(),
        cinNumber: settings.cinNo ? settings.cinNo.trim() : '',
        state: settings.state.trim(),
        stateCode: settings.stateCode.trim(),
        registeredAddress: settings.address.trim(),
        warehouseAddress: settings.warehouseAddress ? settings.warehouseAddress.trim() : '',
        supportEmail: settings.supportEmail.trim(),
        supportPhone: settings.supportPhone.trim(),
        website: settings.website ? settings.website.trim() : '',
        authorizedSignatory: settings.signatoryName ? settings.signatoryName.trim() : '',
        designation: settings.signatoryDesignation ? settings.signatoryDesignation.trim() : ''
      };

      await api.post('/settings/company', companyPayload);

      // 2. Save all key-value settings to MySQL (settings table)
      const generalPayload = {
        storeName: settings.storeName.trim(),
        legalCompanyName: settings.legalCompanyName.trim(),
        gstNo: settings.gstNo.trim(),
        panNo: settings.panNo.trim(),
        cinNo: settings.cinNo ? settings.cinNo.trim() : '',
        state: settings.state.trim(),
        stateCode: settings.stateCode.trim(),
        address: settings.address.trim(),
        warehouseAddress: settings.warehouseAddress ? settings.warehouseAddress.trim() : '',
        supportEmail: settings.supportEmail.trim(),
        supportPhone: settings.supportPhone.trim(),
        website: settings.website ? settings.website.trim() : '',
        signatoryName: settings.signatoryName ? settings.signatoryName.trim() : '',
        signatoryDesignation: settings.signatoryDesignation ? settings.signatoryDesignation.trim() : '',

        orderPrefix: settings.orderPrefix,
        orderNextSeq: settings.orderNextSeq,
        invoicePrefix: settings.invoicePrefix,
        invoiceNextSeq: settings.invoiceNextSeq,

        codEnabled: String(settings.codEnabled),
        razorpayEnabled: String(settings.razorpayEnabled),
        stripeEnabled: String(settings.stripeEnabled),
        onlinePaymentEnabled: String(settings.onlinePaymentEnabled),
        defaultPaymentMethod: settings.defaultPaymentMethod,
        categoryNavigationEnabled: String(settings.categoryNavigationEnabled),
        category_navigation_enabled: String(settings.categoryNavigationEnabled),

        productImageAutoChange: String(settings.productImageAutoChange),
        product_image_auto_change: String(settings.productImageAutoChange),
        productImageChangeInterval: String(settings.productImageChangeInterval),
        product_image_change_interval: String(settings.productImageChangeInterval),

        loginPopupEnabled: String(settings.loginPopupEnabled),
        login_popup_enabled: String(settings.loginPopupEnabled),
        loginPopupDelaySeconds: String(settings.loginPopupDelaySeconds),
        login_popup_delay_seconds: String(settings.loginPopupDelaySeconds),

        footerAbout: settings.footerAbout,
        announcementText: settings.announcementText,
        logoUrl: settings.logoUrl || '',
        email_logo_url: settings.emailLogoUrl || '',
        emailLogoUrl: settings.emailLogoUrl || '',
        maxProductImages: String(settings.maxProductImages),
        maintenanceMode: String(settings.maintenanceMode),
        maintenanceLogoUrl: (settings.maintenanceLogoUrl || '').startsWith('blob:') ? '' : (settings.maintenanceLogoUrl || ''),
        maintenance_logo_url: (settings.maintenanceLogoUrl || '').startsWith('blob:') ? '' : (settings.maintenanceLogoUrl || ''),
        maintenanceTitle: settings.maintenanceTitle,
        maintenanceSubtitle: settings.maintenanceSubtitle,
        maintenanceMessage: settings.maintenanceMessage,
        maintenanceEstimatedTime: settings.maintenanceEstimatedTime,
        maintenanceShowTimer: String(settings.maintenanceShowTimer),
        maintenanceShowSocial: String(settings.maintenanceShowSocial),
        maintenanceAllowSearchEngines: String(settings.maintenanceAllowSearchEngines),
        maintenanceDeveloperEnabled: String(settings.maintenanceDeveloperEnabled),
        maintenance_developer_enabled: String(settings.maintenanceDeveloperEnabled),
        maintenanceDeveloperLogoUrl: (settings.maintenanceDeveloperLogoUrl || '').startsWith('blob:') ? '' : (settings.maintenanceDeveloperLogoUrl || ''),
        maintenance_developer_logo_url: (settings.maintenanceDeveloperLogoUrl || '').startsWith('blob:') ? '' : (settings.maintenanceDeveloperLogoUrl || ''),
        maintenanceDeveloperName: settings.maintenanceDeveloperName || 'CraftLoop',
        maintenance_developer_name: settings.maintenanceDeveloperName || 'CraftLoop',
        maintenanceDeveloperLink: settings.maintenanceDeveloperLink || '',
        maintenance_developer_link: settings.maintenanceDeveloperLink || '',
        karviyam_mobile_homepage_sections: JSON.stringify(mobileSections),
        mobile_homepage_sections: JSON.stringify(mobileSections),
        karviyam_section_layouts: JSON.stringify({
          desktop: {
            recommended: settings.desktopRecommendedMode || 'carousel',
            newArrivals: settings.desktopNewArrivalsMode || 'carousel',
            featured: settings.desktopFeaturedMode || 'carousel'
          },
          mobile: {
            recommended: settings.mobileRecommendedMode || 'horizontal',
            trending: settings.mobileTrendingMode || 'vertical',
            newArrivals: settings.mobileNewArrivalsMode || 'horizontal',
            bestSellers: settings.mobileBestSellersMode || 'vertical'
          },
          removeGreyBox: settings.removeImageGreyBox !== false
        }),
        sectionLayouts: {
          desktop: {
            recommended: settings.desktopRecommendedMode || 'carousel',
            newArrivals: settings.desktopNewArrivalsMode || 'carousel',
            featured: settings.desktopFeaturedMode || 'carousel'
          },
          mobile: {
            recommended: settings.mobileRecommendedMode || 'horizontal',
            trending: settings.mobileTrendingMode || 'vertical',
            newArrivals: settings.mobileNewArrivalsMode || 'horizontal',
            bestSellers: settings.mobileBestSellersMode || 'vertical'
          },
          removeGreyBox: settings.removeImageGreyBox !== false
        }
      };

      await api.post('/settings', generalPayload);
      await api.post('/settings/footer', {
        footerAbout: settings.footerAbout,
        address: settings.address,
        supportPhone: settings.supportPhone,
        supportEmail: settings.supportEmail,
        logoUrl: settings.logoUrl || ''
      }).catch(() => null);
      await api.post('/settings/payment', {
        codEnabled: settings.codEnabled,
        onlinePaymentEnabled: settings.onlinePaymentEnabled,
        razorpayEnabled: settings.razorpayEnabled,
        upiQrEnabled: settings.upiQrEnabled,
        stripeEnabled: settings.stripeEnabled,
        defaultPaymentMethod: settings.defaultPaymentMethod
      }).catch(() => null);

      // Cache locally for offline UI synchronization (sanitize large base64 image data to prevent QuotaExceededError)
      try {
        const sanitizedSettings = { ...settings };
        Object.keys(sanitizedSettings).forEach(k => {
          if (typeof sanitizedSettings[k] === 'string' && sanitizedSettings[k].startsWith('data:image/')) {
            sanitizedSettings[k] = '';
          }
        });
        localStorage.setItem('karviyam_system_settings', JSON.stringify(sanitizedSettings));
      } catch (e) {
        console.warn('[localStorage Quota Warning]:', e);
      }

      const safeLocalSet = (k, v) => {
        try {
          if (typeof v === 'string' && v.startsWith('data:image/')) return;
          localStorage.setItem(k, v || '');
        } catch (e) {}
      };

      safeLocalSet('karviyam_logo', settings.logoUrl);
      safeLocalSet('karviyam_store_name', settings.storeName);
      safeLocalSet('karviyam_legal_company_name', settings.legalCompanyName);
      safeLocalSet('karviyam_support_email', settings.supportEmail);
      safeLocalSet('karviyam_support_phone', settings.supportPhone);
      safeLocalSet('karviyam_address', settings.address);
      safeLocalSet('karviyam_footer_about', settings.footerAbout);
      safeLocalSet('karviyam_gst_no', settings.gstNo);
      safeLocalSet('karviyam_pan_no', settings.panNo);
      safeLocalSet('karviyam_state_code', settings.stateCode);
      safeLocalSet('karviyam_signatory_name', settings.signatoryName);
      safeLocalSet('karviyam_maintenance_mode', String(settings.maintenanceMode));
      safeLocalSet('karviyam_maintenance_logo', settings.maintenanceLogoUrl);
      safeLocalSet('karviyam_maintenance_message', settings.maintenanceMessage);
      safeLocalSet('karviyam_product_image_auto_change', String(settings.productImageAutoChange));
      safeLocalSet('karviyam_product_image_change_interval', String(settings.productImageChangeInterval));

      // Section Scrolling & Layout Configuration (Independent Desktop & Mobile Objects)
      const layoutConfig = {
        desktop: {
          recommended: settings.desktopRecommendedMode || 'carousel',
          newArrivals: settings.desktopNewArrivalsMode || 'carousel',
          featured: settings.desktopFeaturedMode || 'carousel'
        },
        mobile: {
          recommended: settings.mobileRecommendedMode || 'horizontal',
          trending: settings.mobileTrendingMode || 'vertical',
          newArrivals: settings.mobileNewArrivalsMode || 'horizontal',
          bestSellers: settings.mobileBestSellersMode || 'vertical'
        },
        removeGreyBox: settings.removeImageGreyBox !== false
      };

      try {
        localStorage.setItem('karviyam_section_layouts', JSON.stringify(layoutConfig));
        localStorage.setItem('karviyam_mobile_homepage_sections', JSON.stringify(mobileSections));
      } catch (e) {}

      // Re-fetch settings from backend to populate permanent image URLs (replacing temporary base64 preview strings)
      await fetchSettings();

      window.dispatchEvent(new window.Event('karviyam_auto_change_updated'));
      window.dispatchEvent(new window.Event('karviyam_settings_updated'));
      notifyChanges();
      toast.success('Settings saved successfully.');
    } catch (e) {
      console.error('Settings save error:', e);
      const errMsg = e.response?.data?.message || e.message || 'Failed to save settings';
      toast.error(`Error: ${errMsg}`);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Website & Store Settings</h1>
        <p className="text-xs text-slate-500">Configure company legal info, GST/PAN, payment methods, sequential order numbering & invoice settings</p>
      </div>

      {/* Settings Navigation Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab('company')}
          className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-xs font-bold transition-all ${
            activeTab === 'company'
              ? 'bg-[#B71C1C] text-white shadow-md'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <Building2 className="w-4 h-4" />
          <span>Company Information</span>
        </button>

        <button
          onClick={() => setActiveTab('payment')}
          className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-xs font-bold transition-all ${
            activeTab === 'payment'
              ? 'bg-[#B71C1C] text-white shadow-md'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <CreditCard className="w-4 h-4" />
          <span>Payment Methods</span>
        </button>

        <button
          onClick={() => setActiveTab('sequence')}
          className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-xs font-bold transition-all ${
            activeTab === 'sequence'
              ? 'bg-[#B71C1C] text-white shadow-md'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <Hash className="w-4 h-4" />
          <span>Order & Invoice Sequences</span>
        </button>

        <button
          onClick={() => setActiveTab('general')}
          className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-xs font-bold transition-all ${
            activeTab === 'general'
              ? 'bg-[#B71C1C] text-white shadow-md'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <Globe className="w-4 h-4" />
          <span>Branding & General</span>
        </button>

        <button
          onClick={() => setActiveTab('layout')}
          className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-xs font-bold transition-all ${
            activeTab === 'layout'
              ? 'bg-[#B71C1C] text-white shadow-md'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <Sliders className="w-4 h-4" />
          <span>Product Layout & Scroll Controls</span>
        </button>

        <button
          onClick={() => setActiveTab('system')}
          className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-xs font-bold transition-all ${
            activeTab === 'system'
              ? 'bg-[#B71C1C] text-white shadow-md'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <Wrench className="w-4 h-4" />
          <span>System Controls & Maintenance</span>
        </button>

        <button
          onClick={() => { setActiveTab('diagnostics'); fetchSystemHealth(); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-xs font-bold transition-all ${
            activeTab === 'diagnostics'
              ? 'bg-[#B71C1C] text-white shadow-md'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <ShieldCheck className="w-4 h-4" />
          <span>System Health & Diagnostics</span>
        </button>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        
        {/* Tab 1: Company Information & Admin Profile */}
        {activeTab === 'company' && (
          <div className="space-y-6">
            {/* ADMIN PROFILE & PHOTO UPLOAD CARD */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-4 text-xs">
              <h3 className="font-bold text-slate-900 text-sm border-b border-slate-100 pb-3 flex items-center gap-2">
                <User className="w-4 h-4 text-[#B71C1C]" />
                <span>Admin Profile & Avatar</span>
              </h3>

              <div className="flex flex-col sm:flex-row items-center gap-6 bg-slate-50 p-4 rounded-2xl border border-slate-200">
                <div className="relative shrink-0">
                  <div className="w-20 h-20 rounded-2xl bg-white text-slate-800 font-black text-2xl flex items-center justify-center overflow-hidden border-2 border-slate-200 shadow-md p-1">
                    {isValidAvatarUrl(adminPhotoUrl) ? (
                      <img
                        src={resolveImageUrl(adminPhotoUrl)}
                        alt="Admin Avatar"
                        className="w-full h-full object-contain object-center block"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-tr from-[#D32F2F] to-[#B71C1C] flex items-center justify-center rounded-xl">
                        <span className="font-display font-black text-2xl uppercase text-white">K</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 space-y-2 text-center sm:text-left">
                  <div>
                    <div className="font-extrabold text-slate-900 text-sm">Karviyam Admin</div>
                    <div className="text-xs font-semibold text-[#B71C1C] flex items-center gap-1.5 justify-center sm:justify-start mt-0.5 font-mono">
                      <Mail className="w-3.5 h-3.5" />
                      <span>vanakkam@karviyam.com</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-1 justify-center sm:justify-start">
                    <label className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-[#D32F2F] to-[#B71C1C] text-white font-extrabold text-xs rounded-xl shadow-xs hover:shadow-md transition-all cursor-pointer">
                      <Upload className="w-4 h-4" />
                      <span>{uploadingAdminPhoto ? 'Uploading Photo...' : 'Upload Profile Photo'}</span>
                      <input
                        type="file"
                        accept="image/png, image/jpeg, image/jpg, image/webp"
                        onChange={handleAdminPhotoUpload}
                        disabled={uploadingAdminPhoto}
                        className="hidden"
                      />
                    </label>

                    {adminPhotoUrl && (
                      <button
                        type="button"
                        onClick={handleRemoveAdminPhoto}
                        className="px-3.5 py-2 bg-slate-200 hover:bg-rose-100 hover:text-rose-700 text-slate-700 font-bold text-xs rounded-xl transition-all cursor-pointer"
                      >
                        Remove Photo
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-400">Allowed formats: PNG, JPG, JPEG, WEBP. Uploaded photo updates Admin Layout header instantly.</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-4 text-xs">
              <h3 className="font-bold text-slate-900 text-sm border-b border-slate-100 pb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#B71C1C]" />
                <span>Company Legal & Tax Details (Printed on Invoices)</span>
              </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Company Display Name *</label>
                <input
                  type="text"
                  required
                  value={settings.storeName}
                  onChange={(e) => setSettings({ ...settings, storeName: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none focus:border-[#B71C1C] font-bold"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Legal Company Name *</label>
                <input
                  type="text"
                  required
                  value={settings.legalCompanyName}
                  onChange={(e) => setSettings({ ...settings, legalCompanyName: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none focus:border-[#B71C1C] font-bold"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">GST Number (GSTIN) *</label>
                <input
                  type="text"
                  required
                  value={settings.gstNo}
                  onChange={(e) => setSettings({ ...settings, gstNo: e.target.value.toUpperCase() })}
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-mono font-bold"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">PAN Number *</label>
                <input
                  type="text"
                  required
                  value={settings.panNo}
                  onChange={(e) => setSettings({ ...settings, panNo: e.target.value.toUpperCase() })}
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-mono font-bold"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">CIN Number (Optional)</label>
                <input
                  type="text"
                  value={settings.cinNo}
                  onChange={(e) => setSettings({ ...settings, cinNo: e.target.value.toUpperCase() })}
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">State *</label>
                  <input
                    type="text"
                    required
                    value={settings.state}
                    onChange={(e) => setSettings({ ...settings, state: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-bold"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">State Code *</label>
                  <input
                    type="text"
                    required
                    value={settings.stateCode}
                    onChange={(e) => setSettings({ ...settings, stateCode: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-mono font-bold"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="block font-bold text-slate-700 mb-1">Registered Address (Printed on Bills) *</label>
                <textarea
                  rows={2}
                  required
                  value={settings.address}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-semibold text-xs"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block font-bold text-slate-700 mb-1">Warehouse Address</label>
                <textarea
                  rows={2}
                  value={settings.warehouseAddress}
                  onChange={(e) => setSettings({ ...settings, warehouseAddress: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-semibold text-xs"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Customer Support Email *</label>
                <input
                  type="email"
                  required
                  value={settings.supportEmail}
                  onChange={(e) => setSettings({ ...settings, supportEmail: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-bold"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Customer Support Phone *</label>
                <input
                  type="text"
                  required
                  value={settings.supportPhone}
                  onChange={(e) => setSettings({ ...settings, supportPhone: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-bold"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Company Website</label>
                <input
                  type="text"
                  value={settings.website}
                  onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Authorized Signatory Name</label>
                  <input
                    type="text"
                    value={settings.signatoryName}
                    onChange={(e) => setSettings({ ...settings, signatoryName: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-bold"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Designation</label>
                  <input
                    type="text"
                    value={settings.signatoryDesignation}
                    onChange={(e) => setSettings({ ...settings, signatoryDesignation: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-medium"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Tab 2: Payment Methods */}
        {activeTab === 'payment' && (
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-4 text-xs">
            <h3 className="font-bold text-slate-900 text-sm border-b border-slate-100 pb-3 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-[#B71C1C]" />
              <span>Checkout Payment Methods & Control</span>
            </h3>

            <div className="space-y-4">
              
              <div className="flex items-center justify-between bg-slate-50 p-4 rounded-2xl border border-slate-200">
                <div>
                  <h4 className="font-bold text-slate-900">Cash on Delivery (COD)</h4>
                  <p className="text-[11px] text-slate-500">Allow customers to pay in cash upon doorstep delivery</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.codEnabled}
                    onChange={(e) => {
                      const updated = { ...settings, codEnabled: e.target.checked };
                      setSettings(updated);
                      localStorage.setItem('karviyam_system_settings', JSON.stringify(updated));
                      window.dispatchEvent(new window.Event('karviyam_settings_updated'));
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#B71C1C]" />
                </label>
              </div>

              <div className="flex items-center justify-between bg-slate-50 p-4 rounded-2xl border border-slate-200">
                <div>
                  <h4 className="font-bold text-slate-900">Online Payment Gateway Master Toggle</h4>
                  <p className="text-[11px] text-slate-500">Enable or disable all online payment gateways at checkout</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.onlinePaymentEnabled}
                    onChange={(e) => {
                      const updated = { ...settings, onlinePaymentEnabled: e.target.checked };
                      setSettings(updated);
                      localStorage.setItem('karviyam_system_settings', JSON.stringify(updated));
                      window.dispatchEvent(new window.Event('karviyam_settings_updated'));
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#B71C1C]" />
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900">Razorpay (UPI & NetBanking)</span>
                    <input
                      type="checkbox"
                      checked={settings.razorpayEnabled}
                      onChange={(e) => {
                        const updated = { ...settings, razorpayEnabled: e.target.checked };
                        setSettings(updated);
                        localStorage.setItem('karviyam_system_settings', JSON.stringify(updated));
                        window.dispatchEvent(new window.Event('karviyam_settings_updated'));
                      }}
                      className="w-4 h-4 accent-[#B71C1C] cursor-pointer"
                    />
                  </div>
                  <p className="text-[11px] text-slate-500">Supports GPay, PhonePe, Paytm & NetBanking</p>
                </div>

                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900">UPI QR Code</span>
                    <input
                      type="checkbox"
                      checked={settings.upiQrEnabled !== false}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        let newDef = settings.defaultPaymentMethod;
                        if (!isChecked && (newDef === 'UPI_QR' || newDef === 'UPI QR Code')) {
                          newDef = 'COD';
                          toast.error('Default payment method reset to Cash on Delivery (COD) since UPI QR Code was disabled.');
                        }
                        const updated = { ...settings, upiQrEnabled: isChecked, defaultPaymentMethod: newDef };
                        setSettings(updated);
                        localStorage.setItem('karviyam_system_settings', JSON.stringify(updated));
                        window.dispatchEvent(new window.Event('karviyam_settings_updated'));
                      }}
                      className="w-4 h-4 accent-[#B71C1C] cursor-pointer"
                    />
                  </div>
                  <p className="text-[11px] text-slate-500">Allow customers to pay using UPI QR Code</p>
                </div>

                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900">Stripe (Cards)</span>
                    <input
                      type="checkbox"
                      checked={settings.stripeEnabled}
                      onChange={(e) => {
                        const updated = { ...settings, stripeEnabled: e.target.checked };
                        setSettings(updated);
                        localStorage.setItem('karviyam_system_settings', JSON.stringify(updated));
                        window.dispatchEvent(new window.Event('karviyam_settings_updated'));
                      }}
                      className="w-4 h-4 accent-[#B71C1C] cursor-pointer"
                    />
                  </div>
                  <p className="text-[11px] text-slate-500">Supports domestic & international credit cards</p>
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Default Payment Method at Checkout</label>
                <select
                  value={settings.defaultPaymentMethod}
                  onChange={(e) => {
                    const val = e.target.value;
                    if ((val === 'UPI_QR' || val === 'UPI QR Code') && (!settings.upiQrEnabled || !settings.onlinePaymentEnabled)) {
                      toast.error('UPI QR Code must be enabled (and Online Payment Gateway ON) before selecting it as default payment method.');
                      return;
                    }
                    const updated = { ...settings, defaultPaymentMethod: val };
                    setSettings(updated);
                    localStorage.setItem('karviyam_system_settings', JSON.stringify(updated));
                    window.dispatchEvent(new window.Event('karviyam_settings_updated'));
                  }}
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-bold text-xs cursor-pointer"
                >
                  <option value="COD">Cash on Delivery (COD)</option>
                  <option value="Razorpay">Razorpay (UPI, NetBanking)</option>
                  <option value="UPI_QR">UPI QR Code</option>
                  <option value="Stripe">Stripe Credit/Debit Card</option>
                </select>
              </div>

            </div>
          </div>
        )}

        {/* Tab 3: Order & Invoice Sequences */}
        {activeTab === 'sequence' && (
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-4 text-xs">
            <h3 className="font-bold text-slate-900 text-sm border-b border-slate-100 pb-3 flex items-center gap-2">
              <Hash className="w-4 h-4 text-[#B71C1C]" />
              <span>Sequential Numbering Controls (Order ID & Tax Invoice)</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-3">
                <h4 className="font-bold text-slate-900 text-xs">Order ID Sequence Format</h4>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Order Prefix</label>
                  <input
                    type="text"
                    value={settings.orderPrefix}
                    onChange={(e) => setSettings({ ...settings, orderPrefix: e.target.value })}
                    placeholder="e.g. KV-ORD-"
                    className="w-full bg-white border border-slate-200 p-2.5 rounded-xl font-mono font-bold text-xs"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Next Sequence Number</label>
                  <input
                    type="number"
                    min={1}
                    value={settings.orderNextSeq}
                    onChange={(e) => setSettings({ ...settings, orderNextSeq: e.target.value })}
                    className="w-full bg-white border border-slate-200 p-2.5 rounded-xl font-mono font-bold text-xs"
                  />
                </div>
                <p className="text-[11px] text-slate-500">Preview: <span className="font-mono font-bold text-[#B71C1C]">{settings.orderPrefix}{String(settings.orderNextSeq || 1).padStart(6, '0')}</span></p>
              </div>

              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-3">
                <h4 className="font-bold text-slate-900 text-xs">Invoice Number Sequence Format</h4>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Invoice Prefix</label>
                  <input
                    type="text"
                    value={settings.invoicePrefix}
                    onChange={(e) => setSettings({ ...settings, invoicePrefix: e.target.value })}
                    placeholder="e.g. KAR-"
                    className="w-full bg-white border border-slate-200 p-2.5 rounded-xl font-mono font-bold text-xs"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Next Sequence Number</label>
                  <input
                    type="number"
                    min={1}
                    value={settings.invoiceNextSeq}
                    onChange={(e) => setSettings({ ...settings, invoiceNextSeq: e.target.value })}
                    className="w-full bg-white border border-slate-200 p-2.5 rounded-xl font-mono font-bold text-xs"
                  />
                </div>
                <p className="text-[11px] text-slate-500">Preview: <span className="font-mono font-bold text-[#B71C1C]">{settings.invoicePrefix}{String(settings.invoiceNextSeq || 1).padStart(6, '0')}</span></p>
              </div>

            </div>
          </div>
        )}

        {/* Tab 4: Branding & General */}
        {activeTab === 'general' && (
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-4 text-xs">
            <h3 className="font-bold text-slate-900 text-sm border-b border-slate-100 pb-3 flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#B71C1C]" />
              <span>Branding & General Settings</span>
            </h3>

            <div className="space-y-4">
              {/* Customer Login Popup Settings Box */}
              <div className="border border-slate-200 p-5 rounded-2xl bg-white space-y-4 shadow-2xs">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                      <Lock className="w-4 h-4 text-[#B71C1C]" />
                      <span>Customer Login Popup Settings</span>
                    </h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Automatically prompt unauthenticated visitors with a login modal after a configurable delay. Logged-in users will never see this popup.
                    </p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold ${
                    settings.loginPopupEnabled ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
                  }`}>
                    {settings.loginPopupEnabled ? '✓ Popup Enabled (ON)' : '✕ Popup Disabled (OFF)'}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                  <div className="flex items-center justify-between p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
                    <div>
                      <label className="font-bold text-slate-900 text-xs block">Enable Login Popup</label>
                      <span className="text-[10px] text-slate-500 font-medium">Toggle popup for non-logged-in visitors</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.loginPopupEnabled}
                        onChange={(e) => setSettings(s => ({ ...s, loginPopupEnabled: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#B71C1C]" />
                    </label>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-1 text-slate-700">
                      Display Delay (Seconds)
                    </label>
                    <div className="relative flex items-center">
                      <Clock className="absolute left-3 w-4 h-4 text-slate-400" />
                      <input
                        type="number"
                        min="1"
                        max="300"
                        value={settings.loginPopupDelaySeconds}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          setSettings(s => ({
                            ...s,
                            loginPopupDelaySeconds: isNaN(val) ? 5 : Math.max(1, Math.min(300, val))
                          }));
                        }}
                        className="w-full bg-[#F5F5F5] text-slate-900 text-xs pl-9 pr-4 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#B71C1C] focus:bg-white outline-none font-bold"
                      />
                    </div>
                    <span className="text-[10px] text-slate-400 font-medium mt-1 block">Default: 5 seconds (Min: 1s, Max: 300s)</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Footer About Description</label>
                <textarea
                  rows={3}
                  value={settings.footerAbout}
                  onChange={(e) => setSettings({ ...settings, footerAbout: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-medium"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Top Announcement Banner Text</label>
                <input
                  type="text"
                  value={settings.announcementText}
                  onChange={(e) => setSettings({ ...settings, announcementText: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-bold text-xs text-[#B71C1C]"
                />
              </div>

              {/* Logo Upload Box */}
              <div className="border border-slate-200 p-4 rounded-2xl bg-slate-50 flex items-center gap-4">
                {settings.logoUrl ? (
                  <img src={settings.logoUrl} alt="Logo" className="h-12 w-auto max-w-[160px] object-contain border rounded-xl p-1 bg-white" />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-red-50 text-[#B71C1C] flex items-center justify-center font-bold">
                    <ImageIcon className="w-6 h-6" />
                  </div>
                )}

                <div>
                  <label htmlFor="logoFileInput" className="bg-slate-900 hover:bg-[#B71C1C] text-white px-3.5 py-1.5 rounded-xl font-bold transition-colors cursor-pointer inline-flex items-center gap-1.5">
                    <Upload className="w-3.5 h-3.5" />
                    <span>Upload Brand Logo</span>
                  </label>
                  <input type="file" id="logoFileInput" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  <p className="text-[11px] text-slate-500 mt-1">Recommended format: PNG / SVG with transparent background</p>
                </div>
              </div>

              {/* Customer Support Email Header Logo Settings Box */}
              <div className="border border-slate-200 p-5 rounded-2xl bg-white space-y-4 shadow-2xs">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#B71C1C]" />
                      <span>Customer Support Email Branding Logo</span>
                    </h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Upload a custom logo to display at the top of all customer support response emails.
                    </p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold ${
                    settings.emailLogoUrl ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600 border border-slate-200'
                  }`}>
                    {settings.emailLogoUrl ? '✓ Active Custom Email Logo' : 'ℹ Default Karviyam Logo Active'}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                  {/* Email Logo Preview Box */}
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center space-y-2">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Email Header Logo Preview</span>
                    <div className="h-[85px] w-full max-w-[260px] mx-auto bg-white border border-slate-200 rounded-lg p-2 flex items-center justify-center shadow-2xs overflow-hidden">
                      {settings.emailLogoUrl ? (
                        <img src={settings.emailLogoUrl} alt="Active Email Logo" className="max-h-full max-w-full object-contain" />
                      ) : (
                        <div className="flex flex-col items-center">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                            <path d="M12 3C12 3 8.5 7.5 8.5 12C8.5 14.5 10 16.5 12 17.5C14 16.5 15.5 14.5 15.5 12C15.5 7.5 12 3 12 3Z" fill="#B71C1C"/>
                            <path d="M12 17.5C9.5 16.8 6 14 6 11C6 9 7.5 7.5 7.5 7.5C7.5 7.5 5 10.5 5 13.5C5 16.5 8 18.5 12 19C16 18.5 19 16.5 19 13.5C19 10.5 16.5 7.5 16.5 7.5C16.5 7.5 18 9 18 11C18 14 14.5 16.8 12 17.5Z" fill="#B71C1C"/>
                            <path d="M12 19C7 18.5 3 15.5 3 13.5C3 12 4 10.5 4 10.5C4 10.5 2 12.5 2 15C2 17.5 6 20.5 12 21C18 20.5 22 17.5 22 15C22 12.5 20 10.5 20 10.5C20 10.5 21 12 21 13.5C21 15.5 17 18.5 12 19Z" fill="#B71C1C"/>
                          </svg>
                          <span className="font-serif font-black text-xs text-[#B71C1C] tracking-widest uppercase">KARVIYAM</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <label htmlFor="emailLogoFileInput" className="bg-[#B71C1C] hover:bg-[#8E1414] text-white px-4 py-2 rounded-xl font-bold text-xs transition-colors cursor-pointer inline-flex items-center gap-2 shadow-2xs">
                        <Upload className="w-4 h-4" />
                        <span>{settings.emailLogoUrl ? 'Change Email Logo' : 'Upload Email Logo'}</span>
                      </label>
                      <input
                        type="file"
                        id="emailLogoFileInput"
                        accept="image/png, image/jpeg, image/jpg, image/webp"
                        onChange={handleEmailLogoUpload}
                        className="hidden"
                      />

                      {settings.emailLogoUrl && (
                        <button
                          type="button"
                          onClick={handleRemoveEmailLogo}
                          className="bg-slate-100 hover:bg-rose-50 text-slate-700 hover:text-[#B71C1C] border border-slate-200 px-3.5 py-2 rounded-xl font-bold text-xs transition-colors"
                        >
                          Remove Logo
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setShowEmailPreviewModal(true)}
                        className="bg-slate-900 hover:bg-slate-800 text-white px-3.5 py-2 rounded-xl font-bold text-xs transition-colors inline-flex items-center gap-1.5"
                      >
                        <Mail className="w-3.5 h-3.5" />
                        <span>Preview Email</span>
                      </button>
                    </div>

                    <p className="text-[11px] text-slate-500">
                      Supported formats: <strong>PNG, JPG/JPEG, WebP</strong> (Max 5MB). Image max-width in email: <strong>250px</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Product Layout & Scroll Controls */}
        {activeTab === 'layout' && (
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-6 text-xs text-left">
            
            {/* Header & Save Action Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
              <div>
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <Sliders className="w-5 h-5 text-[#B71C1C]" />
                  <span>Product Section Scroll & Layout Controls (Managed by Admin)</span>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5 font-medium">
                  Configure desktop & mobile layouts, reorder homepage sections, edit titles, and control product limits. All changes persist to MySQL database.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleResetProductSections}
                  className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl flex items-center gap-1.5 transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Reset Defaults</span>
                </button>

                <button
                  type="button"
                  onClick={handleSaveProductSections}
                  disabled={savingProductSections}
                  className="px-5 py-2 bg-[#B71C1C] hover:bg-[#900C0C] text-white font-bold text-xs rounded-xl flex items-center gap-2 shadow-sm transition-all cursor-pointer"
                >
                  {savingProductSections ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  <span>{savingProductSections ? 'Saving Changes...' : 'SAVE ALL CHANGES'}</span>
                </button>
              </div>
            </div>

            {/* Search Filter Bar */}
            <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-2xl border border-slate-200">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={productSectionSearch}
                  onChange={(e) => setProductSectionSearch(e.target.value)}
                  placeholder="Search Product Sections (e.g. Recommended, Trending, New, Premium, Starting @ 199)..."
                  className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs font-semibold text-slate-800 outline-none focus:border-[#B71C1C]"
                />
              </div>
              <span className="text-xs font-extrabold text-slate-500 shrink-0">
                {productSections.filter(s => (s.title || s.section_key).toLowerCase().includes(productSectionSearch.toLowerCase())).length} Sections Found
              </span>
            </div>

            {/* Product Sections Cards List */}
            <div className="space-y-4">
              {productSections
                .filter(sec => (sec.title || sec.section_key || '').toLowerCase().includes(productSectionSearch.toLowerCase()))
                .map((sec, idx) => {
                  const isEnabled = sec.enabled !== false;
                  return (
                    <div
                      key={sec.id || sec.section_key}
                      className={`bg-white border rounded-2xl p-5 space-y-4 transition-all ${
                        isEnabled ? 'border-slate-200 shadow-2xs' : 'border-slate-200/60 opacity-60 bg-slate-50/50'
                      }`}
                    >
                      {/* Section Card Top Header */}
                      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
                        <div className="flex items-center gap-3">
                          {/* Reordering Buttons */}
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => handleMoveProductSection(idx, -1)}
                              disabled={idx === 0}
                              className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-30 cursor-pointer"
                              title="Move Up"
                            >
                              <ArrowUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveProductSection(idx, 1)}
                              disabled={idx === productSections.length - 1}
                              className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-30 cursor-pointer"
                              title="Move Down"
                            >
                              <ArrowDown className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Order Position Badge & Key */}
                          <span className="px-2.5 py-1 bg-slate-900 text-white font-black text-[10px] rounded-lg">
                            #{idx + 1}
                          </span>

                          <div>
                            <h4 className="font-extrabold text-slate-900 text-sm">{sec.title}</h4>
                            <span className="font-mono text-[10px] text-slate-400">Key: {sec.section_key || sec.id}</span>
                          </div>
                        </div>

                        {/* Enable / Disable Toggle Switch */}
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-extrabold uppercase tracking-wider ${isEnabled ? 'text-[#B71C1C]' : 'text-slate-400'}`}>
                            {isEnabled ? 'ENABLED [ON]' : 'DISABLED [OFF]'}
                          </span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isEnabled}
                              onChange={(e) => handleUpdateProductSection(sec.id || sec.section_key, { enabled: e.target.checked })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#B71C1C]" />
                          </label>
                        </div>
                      </div>

                      {/* Section Title & Subtitle Edit Row */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">Display Title</label>
                          <input
                            type="text"
                            value={sec.title}
                            onChange={(e) => handleUpdateProductSection(sec.id || sec.section_key, { title: e.target.value })}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-extrabold text-slate-900"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">Display Subtitle</label>
                          <input
                            type="text"
                            value={sec.subtitle || ''}
                            onChange={(e) => handleUpdateProductSection(sec.id || sec.section_key, { subtitle: e.target.value })}
                            placeholder="Optional section subtitle description..."
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-medium text-slate-700"
                          />
                        </div>
                      </div>

                      {/* Side-by-Side Independent Desktop and Mobile Settings */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                        
                        {/* DESKTOP SETTINGS (≥1024px) */}
                        <div className="p-4 bg-slate-50/80 rounded-xl border border-slate-200/80 space-y-3">
                          <h5 className="font-extrabold text-slate-900 text-xs uppercase tracking-wider flex items-center gap-1.5">
                            <Sliders className="w-3.5 h-3.5 text-slate-700" />
                            <span>DESKTOP PRODUCT LAYOUT CONTROLS (≥1024px)</span>
                          </h5>

                          <div className="space-y-1.5">
                            <label className="block text-[11px] font-bold text-slate-700">Layout Format</label>
                            <div className="space-y-1">
                              <label className="flex items-center gap-2 cursor-pointer text-xs">
                                <input
                                  type="radio"
                                  name={`desktop_layout_${sec.id || sec.section_key}`}
                                  value="carousel"
                                  checked={sec.desktop_layout === 'carousel'}
                                  onChange={() => handleUpdateProductSection(sec.id || sec.section_key, { desktop_layout: 'carousel' })}
                                  className="text-[#B71C1C]"
                                />
                                <span className="font-semibold text-slate-800">Horizontal Carousel (1 Row)</span>
                              </label>

                              <label className="flex items-center gap-2 cursor-pointer text-xs">
                                <input
                                  type="radio"
                                  name={`desktop_layout_${sec.id || sec.section_key}`}
                                  value="carousel_2_rows"
                                  checked={sec.desktop_layout === 'carousel_2_rows'}
                                  onChange={() => handleUpdateProductSection(sec.id || sec.section_key, { desktop_layout: 'carousel_2_rows' })}
                                  className="text-[#B71C1C]"
                                />
                                <span className="font-semibold text-slate-800">Horizontal Carousel (2 Rows)</span>
                              </label>

                              <label className="flex items-center gap-2 cursor-pointer text-xs">
                                <input
                                  type="radio"
                                  name={`desktop_layout_${sec.id || sec.section_key}`}
                                  value="grid"
                                  checked={sec.desktop_layout === 'grid'}
                                  onChange={() => handleUpdateProductSection(sec.id || sec.section_key, { desktop_layout: 'grid' })}
                                  className="text-[#B71C1C]"
                                />
                                <span className="font-semibold text-slate-800">Vertical Grid</span>
                              </label>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3 pt-1">
                            <div>
                              <label className="block text-[10.5px] font-bold text-slate-700 mb-1">Products Visible</label>
                              <input
                                type="number"
                                min={1}
                                max={50}
                                value={sec.desktop_product_count || 6}
                                onChange={(e) => handleUpdateProductSection(sec.id || sec.section_key, { desktop_product_count: parseInt(e.target.value, 10) || 6 })}
                                className="w-full bg-white border border-slate-200 rounded-xl px-2.5 py-1.5 text-xs font-bold"
                              />
                            </div>

                            <div>
                              <label className="block text-[10.5px] font-bold text-slate-700 mb-1">Max Loaded Products</label>
                              <input
                                type="number"
                                min={1}
                                max={100}
                                value={sec.desktop_max_products || 12}
                                onChange={(e) => handleUpdateProductSection(sec.id || sec.section_key, { desktop_max_products: parseInt(e.target.value, 10) || 12 })}
                                className="w-full bg-white border border-slate-200 rounded-xl px-2.5 py-1.5 text-xs font-bold"
                              />
                            </div>
                          </div>
                        </div>

                        {/* MOBILE SETTINGS (<1024px) */}
                        <div className="p-4 bg-red-50/20 rounded-xl border border-red-200/60 space-y-3">
                          <h5 className="font-extrabold text-[#B71C1C] text-xs uppercase tracking-wider flex items-center gap-1.5">
                            <Sliders className="w-3.5 h-3.5 text-[#B71C1C]" />
                            <span>MOBILE PRODUCT LAYOUT CONTROLS (&lt;1024px)</span>
                          </h5>

                          <div className="space-y-1.5">
                            <label className="block text-[11px] font-bold text-slate-700">Layout Format</label>
                            <div className="space-y-1">
                              <label className="flex items-center gap-2 cursor-pointer text-xs">
                                <input
                                  type="radio"
                                  name={`mobile_layout_${sec.id || sec.section_key}`}
                                  value="carousel"
                                  checked={sec.mobile_layout === 'carousel'}
                                  onChange={() => handleUpdateProductSection(sec.id || sec.section_key, { mobile_layout: 'carousel' })}
                                  className="text-[#B71C1C]"
                                />
                                <span className="font-semibold text-slate-800">Horizontal Carousel / Swipe</span>
                              </label>

                              <label className="flex items-center gap-2 cursor-pointer text-xs">
                                <input
                                  type="radio"
                                  name={`mobile_layout_${sec.id || sec.section_key}`}
                                  value="grid_2_col"
                                  checked={sec.mobile_layout === 'grid_2_col'}
                                  onChange={() => handleUpdateProductSection(sec.id || sec.section_key, { mobile_layout: 'grid_2_col' })}
                                  className="text-[#B71C1C]"
                                />
                                <span className="font-semibold text-slate-800">Vertical 2-Column Grid</span>
                              </label>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3 pt-1">
                            <div>
                              <label className="block text-[10.5px] font-bold text-slate-700 mb-1">Products Visible</label>
                              <input
                                type="number"
                                min={1}
                                max={50}
                                value={sec.mobile_product_count || 6}
                                onChange={(e) => handleUpdateProductSection(sec.id || sec.section_key, { mobile_product_count: parseInt(e.target.value, 10) || 6 })}
                                className="w-full bg-white border border-slate-200 rounded-xl px-2.5 py-1.5 text-xs font-bold"
                              />
                            </div>

                            <div>
                              <label className="block text-[10.5px] font-bold text-slate-700 mb-1">Max Loaded Products</label>
                              <input
                                type="number"
                                min={1}
                                max={100}
                                value={sec.mobile_max_products || 12}
                                onChange={(e) => handleUpdateProductSection(sec.id || sec.section_key, { mobile_max_products: parseInt(e.target.value, 10) || 12 })}
                                className="w-full bg-white border border-slate-200 rounded-xl px-2.5 py-1.5 text-xs font-bold"
                              />
                            </div>
                          </div>
                        </div>

                      </div>

                      {/* View All Config Row */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1 border-t border-slate-100">
                        <div className="flex items-center gap-2 pt-5">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={sec.show_view_all !== false}
                              onChange={(e) => handleUpdateProductSection(sec.id || sec.section_key, { show_view_all: e.target.checked })}
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#B71C1C]" />
                          </label>
                          <span className="font-extrabold text-xs text-slate-800">Show "View All" Link</span>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">"View All" Button Text</label>
                          <input
                            type="text"
                            value={sec.view_all_text || 'View All →'}
                            onChange={(e) => handleUpdateProductSection(sec.id || sec.section_key, { view_all_text: e.target.value })}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">"View All" Destination Link</label>
                          <input
                            type="text"
                            value={sec.view_all_link || '/shop'}
                            onChange={(e) => handleUpdateProductSection(sec.id || sec.section_key, { view_all_link: e.target.value })}
                            placeholder="e.g. /shop?filter=trending"
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-semibold"
                          />
                        </div>
                      </div>

                    </div>
                  );
                })}
            </div>

              {/* Section 3: Product Card Image Box Settings */}
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-2 mt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-extrabold text-slate-900 text-xs">Product Card Image Background</h4>
                    <p className="text-[11px] text-slate-500">Control image background styling on product cards across the store</p>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.removeImageGreyBox}
                      onChange={(e) => setSettings({ ...settings, removeImageGreyBox: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#B71C1C]" />
                  </label>
                </div>
                <p className="text-[10.5px] text-slate-600 font-medium">
                  {settings.removeImageGreyBox ? '✅ Pure White Background (Grey box removed)' : '⚪ Standard Tinted Grey Box'}
                </p>
              </div>

              {/* Section 4: Product Image Gallery Auto-Change Controls */}
              <div className="p-5 bg-white rounded-2xl border border-slate-200/90 shadow-2xs space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div>
                    <h4 className="font-extrabold text-slate-900 text-sm flex items-center gap-2">
                      <span>PRODUCT IMAGE GALLERY AUTO-CHANGE</span>
                    </h4>
                    <p className="text-[11px] text-slate-500">Automatically cycle through main image, sub images, and video on product detail pages</p>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!settings.productImageAutoChange}
                      onChange={(e) => setSettings({ ...settings, productImageAutoChange: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#B71C1C]" />
                  </label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                  <div>
                    <label className="block font-bold text-slate-700 text-xs mb-1.5">Auto Image Change Status</label>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-black text-xs border ${
                      settings.productImageAutoChange
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                        : 'bg-slate-100 text-slate-600 border-slate-200'
                    }`}>
                      {settings.productImageAutoChange ? '🟢 AUTO CHANGE IS ON' : '🔴 AUTO CHANGE IS OFF'}
                    </span>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 text-xs mb-1.5">Change Image Every</label>
                    <select
                      value={settings.productImageChangeInterval || 3}
                      onChange={(e) => setSettings({ ...settings, productImageChangeInterval: parseInt(e.target.value, 10) || 3 })}
                      disabled={!settings.productImageAutoChange}
                      className="w-full bg-slate-50 border border-slate-300 text-slate-800 text-xs font-bold p-2.5 rounded-xl outline-none focus:border-[#B71C1C] disabled:opacity-50"
                    >
                      <option value={1}>1 Second</option>
                      <option value={2}>2 Seconds</option>
                      <option value={3}>3 Seconds (Default)</option>
                      <option value={4}>4 Seconds</option>
                      <option value={5}>5 Seconds</option>
                      <option value={6}>6 Seconds</option>
                      <option value={7}>7 Seconds</option>
                      <option value={8}>8 Seconds</option>
                      <option value={10}>10 Seconds</option>
                    </select>
                  </div>
                </div>
              </div>

            {/* Bottom Save Bar */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={handleSaveProductSections}
                disabled={savingProductSections}
                className="px-6 py-2.5 bg-[#B71C1C] hover:bg-[#900C0C] text-white font-black text-xs rounded-xl flex items-center gap-2 shadow-md transition-all cursor-pointer"
              >
                {savingProductSections ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span>{savingProductSections ? 'Saving Changes...' : 'SAVE ALL CHANGES'}</span>
              </button>
            </div>

          </div>
        )}

        {/* Tab 5: System Controls & Maintenance Mode */}
        {activeTab === 'system' && (
          <div className="space-y-6 text-xs">
            
            {/* Master Toggle Switch Banner */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-3xl border border-red-200 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <span className="text-[10px] uppercase font-black tracking-widest text-[#B71C1C] bg-white px-3 py-1 rounded-full border border-red-200">
                  SYSTEM SECURITY CONTROL
                </span>
                <h3 className="font-display font-black text-xl text-slate-900 mt-2">
                  Maintenance Mode Master Switch
                </h3>
                <p className="text-xs text-slate-600 mt-1 max-w-md">
                  When active, customer access to browsing, registering, cart & checkout is suspended. Only authenticated Administrators can log in and manage the store.
                </p>
              </div>

              {/* Large OFF ○────────● ON Toggle Switch */}
              <div className="flex items-center gap-3 bg-white p-3 rounded-2xl border border-slate-200 shadow-sm shrink-0">
                <span className={`text-xs font-black uppercase tracking-wider ${!settings.maintenanceMode ? 'text-emerald-700' : 'text-slate-400'}`}>OFF</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.maintenanceMode}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setSettings({ ...settings, maintenanceMode: val });
                      try { localStorage.setItem('karviyam_maintenance_mode', String(val)); } catch (err) {}
                      window.dispatchEvent(new window.Event('karviyam_maintenance_updated'));
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-8 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#B71C1C]" />
                </label>
                <span className={`text-xs font-black uppercase tracking-wider ${settings.maintenanceMode ? 'text-[#B71C1C]' : 'text-slate-400'}`}>ON</span>
              </div>
            </div>

            {/* Maintenance Display Customization Card */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 gap-2">
                <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-[#B71C1C]" />
                  <span>Maintenance Page Display & Customer Messaging</span>
                </h3>
                <button
                  type="button"
                  onClick={() => setShowMaintenancePreviewModal(true)}
                  className="px-3.5 py-1.5 bg-slate-900 hover:bg-black text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-all shadow-xs shrink-0 self-start sm:self-auto cursor-pointer"
                >
                  <Eye className="w-3.5 h-3.5 text-amber-400" />
                  <span>Preview Maintenance Page</span>
                </button>
              </div>

              {/* Dedicated Maintenance Page Logo */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                    <label className="block font-bold text-slate-800 text-xs">Separate Maintenance Page Logo</label>
                    <p className="text-[11px] text-slate-500">This logo will ONLY be applied on the Maintenance Mode page.</p>
                  </div>
                  {settings.maintenanceLogoUrl && (
                    <div className="shrink-0 p-2 bg-white border border-slate-200 rounded-xl flex items-center gap-2">
                      <img src={resolveImageUrl(settings.maintenanceLogoUrl)} alt="Maintenance Logo Preview" className="h-10 w-auto max-w-[140px] object-contain" />
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, maintenanceLogoUrl: '' })}
                        className="text-[10px] text-red-600 font-bold hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Upload Maintenance Logo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          // 1. Instant local preview (0ms UI lag)
                          const reader = new window.FileReader();
                          reader.onloadend = () => {
                            setSettings(prev => ({ ...prev, maintenanceLogoUrl: reader.result }));
                          };
                          reader.readAsDataURL(file);

                          // 2. Non-blocking background upload to server
                          const formData = new FormData();
                          formData.append('file', file);
                          api.post('/upload', formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          }).then(res => {
                            const url = res.data?.data?.url || res.data?.url || res.data?.filePath || res.data?.data?.filePath;
                            if (url) {
                              setSettings(prev => ({ ...prev, maintenanceLogoUrl: url }));
                            }
                          }).catch(err => {
                            console.warn('[AdminSettingsPage] Background logo upload note:', err?.message || err);
                          });
                        }
                      }}
                      className="w-full bg-white border border-slate-200 p-2 rounded-xl text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Or Logo Image URL</label>
                    <input
                      type="text"
                      value={settings.maintenanceLogoUrl}
                      onChange={(e) => setSettings({ ...settings, maintenanceLogoUrl: e.target.value })}
                      placeholder="https://example.com/maintenance-logo.png"
                      className="w-full bg-white border border-slate-200 p-2.5 rounded-xl outline-none font-medium text-xs"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Maintenance Title</label>
                  <input
                    type="text"
                    value={settings.maintenanceTitle}
                    onChange={(e) => setSettings({ ...settings, maintenanceTitle: e.target.value })}
                    placeholder="We'll Be Right Back!"
                    className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-bold text-xs"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Maintenance Subtitle / Badge</label>
                  <input
                    type="text"
                    value={settings.maintenanceSubtitle}
                    onChange={(e) => setSettings({ ...settings, maintenanceSubtitle: e.target.value })}
                    placeholder="SYSTEM UNDER MAINTENANCE"
                    className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-bold text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Maintenance Description Message</label>
                <textarea
                  rows={3}
                  value={settings.maintenanceMessage}
                  onChange={(e) => setSettings({ ...settings, maintenanceMessage: e.target.value })}
                  placeholder="Karviyam is currently undergoing scheduled platform maintenance..."
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-medium text-xs"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Estimated Completion Time</label>
                  <input
                    type="text"
                    value={settings.maintenanceEstimatedTime}
                    onChange={(e) => setSettings({ ...settings, maintenanceEstimatedTime: e.target.value })}
                    placeholder="Estimated Uptime: Back Online Soon"
                    className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-bold text-xs"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Support Contact Details</label>
                  <input
                    type="text"
                    value={settings.supportEmail}
                    onChange={(e) => setSettings({ ...settings, supportEmail: e.target.value })}
                    placeholder="vanakkam@karviyam.com"
                    className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-bold text-xs"
                  />
                </div>
              </div>

              {/* Maintenance Feature Switches */}
              <div className="pt-2 border-t border-slate-100 space-y-3">
                <div className="flex items-center justify-between bg-slate-50 p-3.5 rounded-2xl border border-slate-200">
                  <div>
                    <h4 className="font-bold text-slate-900">Show Countdown Uptime Badge</h4>
                    <p className="text-[11px] text-slate-500">Display estimated completion countdown badge on maintenance screen</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.maintenanceShowTimer}
                    onChange={(e) => setSettings({ ...settings, maintenanceShowTimer: e.target.checked })}
                    className="w-4 h-4 accent-[#B71C1C] cursor-pointer"
                  />
                </div>

                <div className="flex items-center justify-between bg-slate-50 p-3.5 rounded-2xl border border-slate-200">
                  <div>
                    <h4 className="font-bold text-slate-900">Enable Search Engine Indexing Guard</h4>
                    <p className="text-[11px] text-slate-500">Tell search engines (Google/Bing) not to index maintenance status page</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.maintenanceAllowSearchEngines}
                    onChange={(e) => setSettings({ ...settings, maintenanceAllowSearchEngines: e.target.checked })}
                    className="w-4 h-4 accent-[#B71C1C] cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* Maintenance Page Developed By Configuration Card */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 gap-2">
                <div>
                  <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#B71C1C]" />
                    <span>Maintenance Page Developed By</span>
                  </h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Configure the "Developed by [Logo] Name" branding shown at the bottom-right of the Maintenance Page. Managed separately from Customer Footer.
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs font-black uppercase tracking-wider ${settings.maintenanceDeveloperEnabled ? 'text-[#B71C1C]' : 'text-slate-400'}`}>
                    {settings.maintenanceDeveloperEnabled ? 'ON' : 'OFF'}
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!settings.maintenanceDeveloperEnabled}
                      onChange={(e) => setSettings({ ...settings, maintenanceDeveloperEnabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#B71C1C]" />
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                {/* 1. Developer Logo Upload & Preview */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div>
                      <label className="block font-bold text-slate-800 text-xs">Developer Logo</label>
                      <p className="text-[11px] text-slate-500">Upload CraftLoop PNG/JPG/JPEG/WEBP logo. Preserves original aspect ratio & transparency.</p>
                    </div>
                    {settings.maintenanceDeveloperLogoUrl && (
                      <div className="shrink-0 p-2 bg-white border border-slate-200 rounded-xl flex items-center gap-2">
                        <img src={resolveImageUrl(settings.maintenanceDeveloperLogoUrl)} alt="Dev Logo Preview" className="h-8 w-auto max-w-[140px] object-contain" />
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, maintenanceDeveloperLogoUrl: '' })}
                          className="text-[10px] text-red-600 font-bold hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Upload Developer Logo File</label>
                      <input
                        type="file"
                        accept="image/png, image/jpeg, image/jpg, image/webp"
                        disabled={uploadingDevLogo}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;

                          if (file.size > 5 * 1024 * 1024) {
                            toast.error('File size must be less than 5MB');
                            return;
                          }

                          const previousUrl = settings.maintenanceDeveloperLogoUrl;
                          // 1. Instant local preview (0ms UI lag)
                          const blobUrl = URL.createObjectURL(file);
                          setSettings(prev => ({
                            ...prev,
                            maintenanceDeveloperLogoUrl: blobUrl
                          }));

                          // 2. Upload file directly to backend server
                          const toastId = toast.loading('Uploading developer logo to server storage...');
                          setUploadingDevLogo(true);

                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const res = await api.post('/upload', formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });

                            const dataObj = res.data?.data || res.data || {};
                            const serverUrl = dataObj.url || dataObj.fileUrl || dataObj.filePath ||
                                             res.data?.url || res.data?.fileUrl || res.data?.filePath ||
                                             (dataObj.filename ? `/uploads/${dataObj.filename}` : '') ||
                                             (res.data?.filename ? `/uploads/${res.data.filename}` : '');

                            if (serverUrl && typeof serverUrl === 'string' && serverUrl.length > 0 && !serverUrl.startsWith('blob:')) {
                              setSettings(prev => ({
                                ...prev,
                                maintenanceDeveloperLogoUrl: serverUrl,
                                maintenance_developer_logo_url: serverUrl
                              }));
                              toast.success('Developer logo uploaded successfully! Click Save Maintenance Settings.', { id: toastId });
                            } else {
                              setSettings(prev => ({
                                ...prev,
                                maintenanceDeveloperLogoUrl: (previousUrl && !previousUrl.startsWith('blob:')) ? previousUrl : ''
                              }));
                              toast.error('Upload completed but server returned an invalid URL payload. Preview discarded.', { id: toastId });
                            }
                          } catch (err) {
                            console.error('[AdminSettingsPage] Developer logo upload error:', err);
                            setSettings(prev => ({
                              ...prev,
                              maintenanceDeveloperLogoUrl: (previousUrl && !previousUrl.startsWith('blob:')) ? previousUrl : ''
                            }));
                            toast.error(err.response?.data?.message || 'Failed to upload developer logo.', { id: toastId });
                          } finally {
                            setUploadingDevLogo(false);
                            try { URL.revokeObjectURL(blobUrl); } catch (e) {}
                          }
                        }}
                        className="w-full bg-white border border-slate-200 p-2 rounded-xl text-xs disabled:opacity-50"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Or Developer Logo URL</label>
                      <input
                        type="text"
                        value={settings.maintenanceDeveloperLogoUrl}
                        onChange={(e) => setSettings({ ...settings, maintenanceDeveloperLogoUrl: e.target.value })}
                        placeholder="https://example.com/craftloop-logo.png"
                        className="w-full bg-white border border-slate-200 p-2.5 rounded-xl outline-none font-medium text-xs"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* 2. Developer Name */}
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Developer Name *</label>
                    <input
                      type="text"
                      value={settings.maintenanceDeveloperName}
                      onChange={(e) => setSettings({ ...settings, maintenanceDeveloperName: e.target.value })}
                      placeholder="CraftLoop"
                      className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-bold text-xs"
                    />
                  </div>

                  {/* 3. Developer Link */}
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Developer Link (Optional)</label>
                    <input
                      type="text"
                      value={settings.maintenanceDeveloperLink}
                      onChange={(e) => setSettings({ ...settings, maintenanceDeveloperLink: e.target.value })}
                      placeholder="https://craftloop.io"
                      className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl outline-none font-medium text-xs"
                    />
                  </div>
                </div>

                <div className="pt-2 flex justify-end">
                  <button
                    type="submit"
                    disabled={uploadingDevLogo}
                    className="px-5 py-2.5 bg-[#B71C1C] hover:bg-[#900C0C] disabled:opacity-50 text-white font-bold text-xs rounded-xl flex items-center gap-2 shadow-xs transition-all cursor-pointer"
                  >
                    {uploadingDevLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span>{uploadingDevLogo ? 'Uploading Logo...' : 'Save Maintenance Settings'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 7: Protected Production System Health & Diagnostics */}
        {activeTab === 'diagnostics' && (
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-6 text-xs">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-slate-900 text-white flex items-center justify-center font-bold">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-base">Production Health & Deployment Diagnostics</h3>
                  <p className="text-[11px] text-slate-500">Live subsystem connectivity, database schema health, storage, and SMTP diagnostics (Secrets strictly protected)</p>
                </div>
              </div>
              <button
                type="button"
                onClick={fetchSystemHealth}
                disabled={loadingHealth}
                className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-800 px-4 py-2 rounded-2xl font-bold text-xs transition-all cursor-pointer"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loadingHealth ? 'animate-spin' : ''}`} />
                <span>Refresh Diagnostics</span>
              </button>
            </div>

            {loadingHealth ? (
              <div className="py-12 text-center space-y-3">
                <RefreshCw className="w-8 h-8 text-[#B71C1C] animate-spin mx-auto" />
                <p className="font-semibold text-slate-600 text-xs">Running system diagnostics sweep...</p>
              </div>
            ) : systemHealth ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                
                {/* 1. System Environment */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-700">Environment</span>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800">
                      ✓ {systemHealth.environment?.mode || 'production'}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-slate-200/60 space-y-1 text-[11px] text-slate-600">
                    <p><strong className="text-slate-800">Node Env:</strong> {systemHealth.environment?.nodeEnv}</p>
                    <p><strong className="text-slate-800">Commit / Version:</strong> <span className="font-mono text-slate-700">{systemHealth.environment?.gitCommit}</span></p>
                    <p><strong className="text-slate-800">Server Uptime:</strong> {Math.floor((systemHealth.environment?.uptimeSeconds || 0) / 60)} mins</p>
                  </div>
                </div>

                {/* 2. Node Backend API */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-700">Backend API</span>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800">
                      ✓ {systemHealth.backend?.status}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-slate-200/60 space-y-1 text-[11px] text-slate-600">
                    <p><strong className="text-slate-800">HTTP Status:</strong> 200 OK</p>
                    <p><strong className="text-slate-800">Response Time:</strong> Ping OK</p>
                    <p><strong className="text-slate-800">Public App URL:</strong> <span className="font-mono text-slate-700">{systemHealth.uploads?.publicAppUrl}</span></p>
                  </div>
                </div>

                {/* 3. Database Connection */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-700">MySQL Database</span>
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${systemHealth.database?.status === 'connected' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                      {systemHealth.database?.status === 'connected' ? '✓ Connected' : '✕ Error'}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-slate-200/60 space-y-1 text-[11px] text-slate-600">
                    <p><strong className="text-slate-800">Database Name:</strong> {systemHealth.database?.databaseName}</p>
                    <p><strong className="text-slate-800">Tables Found:</strong> {systemHealth.database?.tableCount || 0} tables</p>
                    <p><strong className="text-slate-800">Connection Ping:</strong> Success</p>
                  </div>
                </div>

                {/* 4. Upload Storage */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-700">Upload Storage</span>
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${systemHealth.uploads?.writable ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                      {systemHealth.uploads?.writable ? '✓ Writable' : '⚠ Check Perms'}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-slate-200/60 space-y-1 text-[11px] text-slate-600">
                    <p><strong className="text-slate-800">Storage Path:</strong> <span className="font-mono text-[10px] text-slate-700 break-all">{systemHealth.uploads?.uploadDir}</span></p>
                    <p><strong className="text-slate-800">Directory Exists:</strong> {systemHealth.uploads?.exists ? 'YES' : 'NO'}</p>
                    <p><strong className="text-slate-800">Image Base URL:</strong> <span className="font-mono text-slate-700">{systemHealth.uploads?.publicAppUrl}/uploads/</span></p>
                  </div>
                </div>

                {/* 5. SMTP Mailer */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-700">SMTP Transporter</span>
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${systemHealth.smtp?.status === 'verified' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                      {systemHealth.smtp?.status === 'verified' ? '✓ Verified' : '⚠ Connection Info'}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-slate-200/60 space-y-1 text-[11px] text-slate-600">
                    <p><strong className="text-slate-800">SMTP Host:</strong> <span className="font-mono text-slate-700">{systemHealth.smtp?.host}:{systemHealth.smtp?.port}</span></p>
                    <p><strong className="text-slate-800">Sender Mailbox:</strong> <span className="font-mono text-slate-700">{systemHealth.smtp?.user}</span></p>
                    <p><strong className="text-slate-800">SSL / TLS Secure:</strong> {systemHealth.smtp?.secure ? 'true (Port 465)' : 'false'}</p>
                  </div>
                </div>

                {/* 6. Payment & Authentication */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-700">Auth & Payments</span>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800">
                      ✓ Configured
                    </span>
                  </div>
                  <div className="pt-2 border-t border-slate-200/60 space-y-1 text-[11px] text-slate-600">
                    <p><strong className="text-slate-800">Authentication:</strong> JWT Bearer Header</p>
                    <p><strong className="text-slate-800">Razorpay Key ID:</strong> {systemHealth.payment?.razorpayKeyIdConfigured ? '✓ Configured' : '✕ Missing'}</p>
                    <p><strong className="text-slate-800">Razorpay Secret:</strong> {systemHealth.payment?.razorpaySecretConfigured ? '✓ Configured' : '✕ Missing'}</p>
                  </div>
                </div>

              </div>
            ) : (
              <div className="py-8 text-center text-slate-500 font-medium">
                Unable to load system health data. Please check backend server status.
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            className="flex items-center gap-2 bg-[#B71C1C] hover:bg-[#900C0C] text-white px-6 py-3 rounded-2xl font-bold text-xs uppercase tracking-wider shadow-md transition-all cursor-pointer"
          >
            <Save className="w-4 h-4" />
            <span>SAVE SETTINGS TO DATABASE</span>
          </button>
        </div>

      </form>

      {/* ========================================================= */}
      {/* CUSTOMER SUPPORT EMAIL PREVIEW MODAL                      */}
      {/* ========================================================= */}
      {showEmailPreviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 space-y-4 shadow-2xl border border-slate-200 text-left max-h-[90vh] overflow-y-auto">
            
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#B71C1C]" />
                <h3 className="font-bold text-slate-900 text-base">Customer Support Response Email Live Preview</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowEmailPreviewModal(false)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:text-slate-900 flex items-center justify-center font-bold"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-slate-500">
              This preview shows how customer support response emails appear in Gmail, Outlook, and mobile apps using your active email logo configuration.
            </p>

            {/* Email Canvas Preview Box */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4 text-xs font-sans">
              
              {/* Header Logo */}
              <div className="text-center pb-3 border-b border-slate-100 flex items-center justify-center">
                {settings.emailLogoUrl ? (
                  <img src={settings.emailLogoUrl} alt="Karviyam Logo" className="max-h-16 max-w-[240px] object-contain mx-auto" />
                ) : (
                  <div className="flex flex-col items-center">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                      <path d="M12 3C12 3 8.5 7.5 8.5 12C8.5 14.5 10 16.5 12 17.5C14 16.5 15.5 14.5 15.5 12C15.5 7.5 12 3 12 3Z" fill="#B71C1C"/>
                      <path d="M12 17.5C9.5 16.8 6 14 6 11C6 9 7.5 7.5 7.5 7.5C7.5 7.5 5 10.5 5 13.5C5 16.5 8 18.5 12 19C16 18.5 19 16.5 19 13.5C19 10.5 16.5 7.5 16.5 7.5C16.5 7.5 18 9 18 11C18 14 14.5 16.8 12 17.5Z" fill="#B71C1C"/>
                      <path d="M12 19C7 18.5 3 15.5 3 13.5C3 12 4 10.5 4 10.5C4 10.5 2 12.5 2 15C2 17.5 6 20.5 12 21C18 20.5 22 17.5 22 15C22 12.5 20 10.5 20 10.5C20 10.5 21 12 21 13.5C21 15.5 17 18.5 12 19Z" fill="#B71C1C"/>
                    </svg>
                    <span className="font-serif font-black text-xl text-[#B71C1C] tracking-widest uppercase mt-1">KARVIYAM</span>
                    <span className="text-[10px] text-slate-400 font-medium">Timeless Style. Trusted Quality.</span>
                  </div>
                )}
              </div>

              {/* Subject & Header Info */}
              <div className="flex justify-between items-center text-slate-600 text-[11px] pb-2 border-b border-slate-100">
                <span>Subject: <strong className="text-slate-900">Re: Customer Support Inquiry (#5000001)</strong></span>
                <span>{new Date().toLocaleDateString('en-GB')}</span>
              </div>

              {/* Body Content */}
              <div className="space-y-3 text-slate-700 leading-relaxed text-xs">
                <p>Hello <strong>Valued Customer</strong>,</p>
                <p>Hello from Karviyam Customer Support,</p>
                <p>We understand that you have an issue regarding Order <strong className="text-[#B71C1C]">#5000001</strong>.</p>
                <div className="bg-slate-50 border-l-4 border-[#B71C1C] p-3 rounded-r-xl font-medium text-slate-800">
                  We have verified your request. Your inquiry has been processed and resolved by our support team.
                </div>
                <p>We appreciate your cooperation and understanding in this regard.</p>
                <p>Thank you for choosing Karviyam,</p>
                <div>
                  <strong className="text-[#B71C1C] block font-bold text-sm">Karviyam Support Team</strong>
                  <span className="text-slate-500 font-mono text-[11px]">vanakkam@karviyam.com</span>
                </div>
              </div>

              {/* Pre-footer Info & Social Links */}
              <div className="border-t border-slate-100 pt-4 text-center text-[10px] text-slate-400 space-y-1">
                <p>© {new Date().getFullYear()} Karviyam. All rights reserved.</p>
              </div>

            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowEmailPreviewModal(false)}
                className="px-4 py-2 bg-slate-900 text-white rounded-xl font-bold text-xs hover:bg-slate-800 transition-colors"
              >
                Close Preview
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Maintenance Page Live Preview Modal */}
      {showMaintenancePreviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 bg-slate-900/80 backdrop-blur-xs">
          <div className="bg-white rounded-3xl max-w-5xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-slate-900 text-white shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-2xl bg-[#B71C1C] text-white flex items-center justify-center font-bold shrink-0">
                  <Wrench className="w-4 h-4" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-sm sm:text-base text-white">Maintenance Page Preview</h3>
                    <span className="px-2.5 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[10px] font-black uppercase tracking-wider rounded-full">
                      PREVIEW MODE
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400">Renders live using current unsaved form values without modifying production settings.</p>
                </div>
              </div>

              {/* Viewport Switcher & Close */}
              <div className="flex items-center gap-3">
                <div className="flex items-center bg-slate-800 p-1 rounded-xl border border-slate-700">
                  <button
                    type="button"
                    onClick={() => setPreviewDeviceMode('desktop')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${
                      previewDeviceMode === 'desktop'
                        ? 'bg-[#B71C1C] text-white shadow-xs'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Monitor className="w-3.5 h-3.5" />
                    <span>Desktop</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewDeviceMode('mobile')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${
                      previewDeviceMode === 'mobile'
                        ? 'bg-[#B71C1C] text-white shadow-xs'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Smartphone className="w-3.5 h-3.5" />
                    <span>Mobile</span>
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setShowMaintenancePreviewModal(false)}
                  className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
                  title="Close Preview"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-0 overflow-y-auto flex-1 bg-[#F8FAFC] flex flex-col items-center justify-center relative min-h-[480px]">
              {previewDeviceMode === 'desktop' ? (
                <div className="w-full flex-1 min-h-[500px] flex flex-col items-center justify-center bg-[#F8FAFC] p-4 sm:p-8 overflow-y-auto">
                  <MaintenancePage previewMode={true} previewSettings={settings} />
                </div>
              ) : (
                <div className="w-full flex-1 flex items-center justify-center bg-slate-200/80 p-4 sm:p-6 overflow-y-auto">
                  <div className="w-[375px] max-w-[92vw] h-[680px] max-h-[82vh] bg-[#F8FAFC] border-[10px] border-slate-900 rounded-[44px] shadow-2xl overflow-y-auto relative flex flex-col shrink-0 my-auto">
                    {/* Smartphone Notch */}
                    <div className="w-28 h-4 bg-slate-900 mx-auto rounded-b-2xl shrink-0 z-10"></div>
                    <div className="flex-1 flex flex-col items-center justify-center w-full my-auto py-2">
                      <MaintenancePage previewMode={true} previewSettings={settings} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-100 bg-white flex flex-wrap items-center justify-between gap-3 shrink-0">
              <span className="text-[11px] font-medium text-slate-500">
                💡 Previewing current form values. Database and live customer storefront remain unchanged.
              </span>
              <button
                type="button"
                onClick={() => setShowMaintenancePreviewModal(false)}
                className="px-5 py-2.5 bg-slate-900 hover:bg-black text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

